<template>
  
  <bmsTest v-if="showBmsTest" :oth_showBmsTest="v => showBmsTest = v" />
  
  <div class="crm-fullscreen">
    <!-- Top App Bar -->
    <header class="app-bar">
      <button class="app-bar-back" @click="oth_showCrmList(false)">
        <i class="ph ph-arrow-left"></i>
      </button>
      <h1 class="app-bar-title">CRM</h1>
      <label class="auto-send-toggle" :class="{ on: autoEnabled }" @click.prevent="toggleAutoSend">
        <span class="auto-send-label">자동발송</span>
        <span class="auto-send-switch">
          <span class="auto-send-knob"></span>
        </span>
      </label>
      <div class="app-bar-actions">
        <div class="search-box" :class="{ expanded: searchFocused }">
          <i class="ph ph-magnifying-glass"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="getSearchPlaceholder"
            @focus="searchFocused = true"
            @blur="searchFocused = false"
          >
        </div>
        <select v-model="sortOption" class="sort-chip">
          <option value="latest">최신순</option>
          <option value="oldest">오래된순</option>
          <option value="name">이름순</option>
        </select>
      </div>
    </header>

    <!-- Tab Navigation -->
    <nav class="tab-nav">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        :class="['tab-chip', { active: activeTab === tab.id }]"
        @click="activeTab = tab.id"
      >
        <i :class="tab.icon"></i>
        {{ tab.name }}
        <span v-if="tab.id === 'reserved' && (existingPacks.length + commonMessages.length) > 0" class="tab-count">
          {{ existingPacks.length + commonMessages.length }}
        </span>
        <span v-if="tab.id === 'personal' && myMessages.length > 0" class="tab-count">
          {{ myMessages.length }}
        </span>
      </button>
    </nav>

    <!-- Content Area -->
    <main class="content-area">

      <!-- ==================== 예약 메시지 탭 (팩 + 공통 메시지) ==================== -->
      <div v-if="activeTab === 'reserved'" class="tab-panel">
        <!-- 메시지 팩 -->
        <section v-if="existingPacks.length > 0" class="msg-section">
          <div class="section-head">
            <div class="section-label">
              <i class="ph-fill ph-package"></i>
              문자 팩
            </div>
            <span class="badge-count">{{ existingPacks.length }}</span>
          </div>

          <div class="card-list">
            <div 
              v-for="pack in existingPacks" 
              :key="'pack-' + pack.idx"
              class="msg-card pack-card"
              :class="{ paused: pack.status === 'paused', completed: pack.status === 'completed' }"
              @click="pack.is_basic == 1 ? openPackViewer(pack) : openPackEditor(pack)"
            >
              <div class="card-indicator pack"></div>
              <div class="card-thumb">
                <div class="thumb-icon pack-thumb">
                  <i class="ph-fill ph-package"></i>
                </div>
              </div>
              <div class="card-body">
                <div class="card-top-row">
                  <h4>{{ pack.pack_name }}</h4>
                  <span class="pill pack-pill">{{ pack.item_count || 0 }}종</span>
                </div>
                <div class="card-chips">
                  <span class="chip">
                    <i class="ph ph-shuffle"></i>
                    랜덤 택1
                  </span>
                  <span class="chip">
                    <i class="ph ph-clock"></i>
                    {{ getPackScheduleText(pack) }}
                  </span>
                  <span v-if="pack.is_basic == 1 && getUserPackSetting(pack)?.target_count > 0" class="chip">
                    <i class="ph ph-users"></i>
                    {{ getUserPackSetting(pack).target_count }}명
                  </span>
                  <span v-else-if="pack.is_basic == 1" class="chip warn">
                    <i class="ph ph-gear"></i> 설정필요
                  </span>
                  <span v-if="pack.status === 'completed'" class="chip completed">
                    <i class="ph ph-check-circle"></i> 발송완료
                  </span>
                </div>
              </div>
              <button v-if="pack.is_basic != 1 || isAdmin" class="card-menu-btn" @click.stop="deletePack(pack)">
                <i class="ph ph-trash"></i>
              </button>
            </div>
          </div>
        </section>

        <!-- 공통 메시지 -->
        <section v-if="commonMessages.length > 0" class="msg-section">
          <div class="section-head">
            <div class="section-label">
              <i class="ph-fill ph-globe-hemisphere-west"></i>
              공통 메시지
            </div>
            <span class="badge-count">{{ commonMessages.length }}</span>
          </div>

          <div class="card-list">
            <div 
              v-for="msg in commonMessages" 
              :key="'common-' + msg.idx"
              class="msg-card"
              :class="{ 
                paused: msg.status === 'paused' || msg.status === 'draft',
                disabled: getUserMsgSetting(msg)?.enabled === 0
              }"
              @click="openMessageViewer(msg)"
            >
              <div class="card-indicator common"></div>
              <div class="card-thumb">
                <img v-if="msg.image" :src="msg.image" alt="">
                <div v-else class="thumb-icon" :class="msg.trigger_type">
                  <i :class="getTriggerIcon(msg.trigger_type)"></i>
                </div>
              </div>
              <div class="card-body">
                <div class="card-top-row">
                  <h4>{{ msg.title }}</h4>
                  <span class="pill origin-pill">원본</span>
                </div>
                <p class="card-desc">{{ truncate(msg.content, 50) }}</p>
                <div class="card-chips">
                  <span class="chip">
                    <i class="ph ph-clock"></i>
                    {{ getScheduleText(msg) }}
                  </span>
                  <span v-if="getUserMsgSetting(msg)?.target_count > 0" class="chip">
                    <i class="ph ph-users"></i>
                    {{ getUserMsgSetting(msg).target_count }}명
                  </span>
                  <span v-else class="chip warn">
                    <i class="ph ph-gear"></i> 설정필요
                  </span>
                </div>
              </div>
              <button v-if="isAdmin" class="card-menu-btn" @click.stop="openMenu($event, msg.idx, 'message')">
                <i class="ph ph-dots-three-vertical"></i>
              </button>
            </div>
          </div>
        </section>

        <!-- Empty (둘 다 없을 때) -->
        <div v-if="existingPacks.length === 0 && commonMessages.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="ph ph-package"></i>
          </div>
          <p class="empty-title">등록된 예약 메시지가 없어요</p>
          <p class="empty-desc">문자 팩이나 공통 메시지를 추가하여<br>고객에게 자동으로 인사를 보내보세요</p>
        </div>
      </div>

      <!-- ==================== 개인 메시지 탭 ==================== -->
      <div v-if="activeTab === 'personal'" class="tab-panel">
        <section class="msg-section">
          <div class="card-list">
            <div 
              v-for="msg in myMessages" 
              :key="'my-' + msg.idx"
              class="msg-card"
              :class="{ paused: msg.status === 'paused' || msg.status === 'draft' }"
              @click="openMessageViewer(msg)"
            >
              <div class="card-indicator mine"></div>
              <div class="card-thumb">
                <img v-if="msg.image" :src="msg.image" alt="">
                <div v-else class="thumb-icon" :class="msg.trigger_type">
                  <i :class="getTriggerIcon(msg.trigger_type)"></i>
                </div>
              </div>
              <div class="card-body">
                <h4>{{ msg.title }}</h4>
                <p class="card-desc">{{ truncate(msg.content, 50) }}</p>
                <div class="card-chips">
                  <span class="chip">
                    <i class="ph ph-users"></i>
                    {{ msg.target_count || 0 }}명
                  </span>
                  <span class="chip">
                    <i class="ph ph-clock"></i>
                    {{ getScheduleText(msg) }}
                  </span>
                  <span v-if="msg.attach_gift == 1" class="chip gift">
                    <i class="ph-fill ph-gift"></i> 선물
                  </span>
                  <span v-if="msg.pack_id" class="chip pack">
                    📦 {{ msg.pack_name }}
                  </span>
                  <span v-if="msg.status === 'paused'" class="chip warn">
                    <i class="ph ph-pause-circle"></i> 중지됨
                  </span>
                  <span v-if="msg.status === 'draft'" class="chip warn">
                    <i class="ph ph-note-pencil"></i> 임시저장
                  </span>
                </div>
              </div>
              <button class="card-menu-btn" @click.stop="openMenu($event, msg.idx, 'message')">
                <i class="ph ph-dots-three-vertical"></i>
              </button>
            </div>
          </div>

          <!-- Empty -->
          <div v-if="myMessages.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="ph ph-chats-circle"></i>
            </div>
            <p class="empty-title">개인 메시지가 없어요</p>
            <p class="empty-desc">직접 메시지를 추가하여 고객에게<br>맞춤 인사를 보내보세요</p>
          </div>
        </section>
      </div>

      <!-- ==================== 메시지 예제 (템플릿) 탭 ==================== -->
      <div v-if="activeTab === 'templates'" class="tab-panel">
        <button class="add-tpl-btn" @click="openTemplateEditor(null)">
          <i class="ph ph-plus-circle"></i>
          <span>새 템플릿 추가</span>
        </button>

        <div class="template-grid">
          <div 
            v-for="tpl in filteredTemplates" 
            :key="tpl.idx"
            class="tpl-card"
          >
            <div class="tpl-visual" @click="openTemplateViewer(tpl)">
              <img v-if="tpl.image" :src="tpl.image" alt="">
              <div v-else class="tpl-icon-bg" :class="tpl.category">
                <i :class="getCategoryIcon(tpl.category)"></i>
              </div>
              <div class="tpl-overlay">
                <span class="tpl-cat-badge" :class="tpl.category">
                  {{ getCategoryLabel(tpl.category) }}
                </span>
                <span v-if="tpl.mb_id === getMbid()" class="tpl-mine-badge">내 템플릿</span>
              </div>
            </div>
            <div class="tpl-info">
              <h4>{{ tpl.title }}</h4>
              <p>{{ truncate(tpl.content, 40) }}</p>
            </div>
            <button 
              v-if="tpl.mb_id === getMbid() || isAdmin" 
              class="tpl-more-btn" 
              @click.stop="openMenu($event, tpl.idx, 'template')"
            >
              <i class="ph ph-dots-three-vertical"></i>
            </button>
          </div>

          <div v-if="filteredTemplates.length === 0" class="empty-state grid-empty">
            <div class="empty-icon">
              <i class="ph ph-file-text"></i>
            </div>
            <p class="empty-title">템플릿이 없어요</p>
            <p class="empty-desc">자주 쓰는 메시지를 템플릿으로<br>저장해서 편하게 사용하세요</p>
          </div>
        </div>
      </div>

      <!-- ==================== 발송 이력 탭 ==================== -->
      <div v-if="activeTab === 'history'" class="tab-panel">
        <div class="history-list">
          <div 
            v-for="log in filteredHistory" 
            :key="log.idx"
            class="history-row"
            :class="{ failed: log.status === 'failed' }"
          >
            <div class="history-status-dot" :class="log.status"></div>
            <div class="history-body">
              <div class="history-top">
                <span class="history-name">{{ log.customer_name }}</span>
                <span class="history-date">{{ formatDate(log.sent_at) }}</span>
              </div>
              <p class="history-content">{{ truncate(log.content || '-', 30) }}</p>
              <div class="history-tags">
                <span class="htag" :class="log.send_type">
                  {{ getSendTypeLabel(log.send_type) }}
                </span>
                <span class="htag" :class="log.status">
                  {{ getStatusLabel(log.status) }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="filteredHistory.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="ph ph-clock-counter-clockwise"></i>
            </div>
            <p class="empty-title">발송 이력이 없어요</p>
            <p class="empty-desc">메시지를 발송하면<br>이곳에서 확인할 수 있어요</p>
          </div>
        </div>
      </div>
    </main>

    <!-- FAB (Floating Action Button) -->
    <div v-if="activeTab === 'reserved' || activeTab === 'personal'" class="fab-container">
      <transition name="fab-menu">
        <div v-if="fabOpen" class="fab-menu">
          <!-- 예약 메시지 탭 -->
          <template v-if="activeTab === 'reserved'">
            <button class="fab-menu-item" @click="fabAction('pack')">
              <span class="fab-menu-label">문자 팩</span>
              <span class="fab-menu-icon pack">
                <i class="ph-bold ph-package"></i>
              </span>
            </button>
            <button v-if="isAdmin" class="fab-menu-item" @click="fabAction('common')">
              <span class="fab-menu-label">공통 메시지</span>
              <span class="fab-menu-icon auto">
                <i class="ph-bold ph-globe-hemisphere-west"></i>
              </span>
            </button>
          </template>
          <!-- 개인 메시지 탭 -->
          <template v-if="activeTab === 'personal'">
            <button class="fab-menu-item" @click="fabAction('auto')">
              <span class="fab-menu-label">자동 메시지</span>
              <span class="fab-menu-icon auto">
                <i class="ph-bold ph-clock-clockwise"></i>
              </span>
            </button>
            <button class="fab-menu-item" @click="fabAction('group')">
              <span class="fab-menu-label">그룹 발송</span>
              <span class="fab-menu-icon group">
                <i class="ph-bold ph-users-three"></i>
              </span>
            </button>
            <button class="fab-menu-item" @click="fabAction('instant')">
              <span class="fab-menu-label">직접 발송</span>
              <span class="fab-menu-icon instant">
                <i class="ph-bold ph-paper-plane-tilt"></i>
              </span>
            </button>
            <button class="fab-menu-item" @click="showBmsTest = true; fabOpen = false">
              <span class="fab-menu-label">BMS 테스트</span>
              <span class="fab-menu-icon" style="background:#8b5cf6;">
                <i class="ph-bold ph-chat-circle-dots"></i>
              </span>
            </button>
          </template>
        </div>
      </transition>
      <div v-if="fabOpen" class="fab-scrim" @click="fabOpen = false"></div>
      <button 
        class="fab-main" 
        :class="{ open: fabOpen }"
        @click="activeTab === 'reserved' && !isAdmin ? fabAction('pack') : (fabOpen = !fabOpen)"
      >
        <i class="ph-bold ph-plus"></i>
      </button>
    </div>

    <!-- ==================== 드롭다운 메뉴 (Portal) ==================== -->
    <Teleport to="body">
      <div v-if="menuState.visible" class="dropdown-overlay" @click="closeMenu">
        <div class="dropdown-sheet" :style="menuStyle" @click.stop>
          <template v-if="menuState.type === 'message'">
            <button @click="handleMenuAction('edit')">
              <i :class="menuState.item?.is_basic == 1 ? 'ph ph-warning' : 'ph ph-pencil-simple'"></i>
              {{ menuState.item?.is_basic == 1 ? '원본 수정' : '수정' }}
            </button>
            <button v-if="isAdmin" @click="handleMenuAction('toggleCommon')">
              <i :class="menuState.item?.is_basic == 1 ? 'ph ph-user' : 'ph ph-globe'"></i>
              {{ menuState.item?.is_basic == 1 ? '공통 해제' : '공통 설정' }}
            </button>
            <button @click="handleMenuAction('toggle')">
              <i :class="menuState.item?.status === 'active' ? 'ph ph-pause' : 'ph ph-play'"></i>
              {{ menuState.item?.status === 'active' ? '비사용' : '사용' }}
            </button>
            <button class="danger" @click="handleMenuAction('delete')">
              <i class="ph ph-trash-simple"></i> 삭제
            </button>
          </template>
          <template v-else-if="menuState.type === 'template'">
            <button @click="handleMenuAction('editTemplate')">
              <i class="ph ph-pencil-simple"></i> 수정
            </button>
            <button @click="handleMenuAction('togglePublic')">
              <i :class="menuState.item?.is_public == 1 ? 'ph ph-lock' : 'ph ph-globe'"></i>
              {{ menuState.item?.is_public == 1 ? '비공개로 전환' : '공개로 전환' }}
            </button>
            <button class="danger" @click="handleMenuAction('deleteTemplate')">
              <i class="ph ph-trash-simple"></i> 삭제
            </button>
          </template>
        </div>
      </div>
    </Teleport>

    <!-- ==================== 메시지 뷰어 모달 ==================== -->
    <Teleport to="body">
      <transition name="slide-up">
        <div v-if="showMessageViewer" class="sheet-overlay" @click.self="showMessageViewer = false">
          <div class="bottom-sheet">
            <div class="sheet-handle"></div>
            <div class="sheet-header">
              <h3>{{ viewingMessage?.title }}</h3>
              <button class="sheet-close" @click="showMessageViewer = false">
                <i class="ph ph-x"></i>
              </button>
            </div>
            
            <div class="sheet-body">
              <div class="sms-preview-area">
                <div class="sms-bubble">
                  <img v-if="viewingMessage?.image" :src="viewingMessage.image" class="sms-image" alt="">
                  <p v-if="viewingMessage?.use_header" class="sms-header-text">{{ msgHeader }}</p>
                  <p class="sms-body-text">{{ viewingMessage?.content }}</p>
                  <span class="sms-time">{{ viewingMessage?.trigger_time?.slice(0, 5) || '09:00' }}</span>
                </div>
              </div>

              <div class="info-grid">
                <div class="info-cell">
                  <span class="info-key">발송 유형</span>
                  <span class="info-val">
                    <span class="trigger-pill" :class="viewingMessage?.trigger_type">
                      {{ getTriggerLabel(viewingMessage?.trigger_type) }}
                    </span>
                  </span>
                </div>
                <div class="info-cell">
                  <span class="info-key">발송 시점</span>
                  <span class="info-val">{{ getScheduleText(viewingMessage || {}) }}</span>
                </div>
                <div class="info-cell">
                  <span class="info-key">메시지 타입</span>
                  <span class="info-val">
                    <span class="type-pill" :class="getMessageTypeFromMsg(viewingMessage)">
                      {{ getMessageTypeLabel(viewingMessage) }}
                    </span>
                    <span class="sub-text">{{ getFullMessageLength(viewingMessage) }}자</span>
                  </span>
                </div>
                <div v-if="viewingMessage?.is_basic != 1" class="info-cell">
                  <span class="info-key">발송 대상</span>
                  <span class="info-val">{{ viewingMessage?.target_count || 0 }}명</span>
                </div>
                <div class="info-cell">
                  <span class="info-key">발신자 표시</span>
                  <span class="info-val">{{ viewingMessage?.use_header ? '사용' : '미사용' }}</span>
                </div>
                <div v-if="viewingMessage?.is_basic != 1 && viewingMessage?.attach_gift == 1" class="info-cell">
                  <span class="info-key">🎁 선물 첨부</span>
                  <span class="info-val">
                    {{ viewingMessage?.gift_product === 'random' ? '랜덤' : viewingMessage?.gift_product }}
                    <span class="sub-text">최대 {{ viewingMessage?.gift_max_count || 10 }}개</span>
                  </span>
                </div>
              </div>

              <!-- ===== 내 발송 설정 (공통 메시지) ===== -->
              <div v-if="viewingMessage?.is_basic == 1" class="user-settings-section">
                <div class="us-header">
                  <i class="ph ph-gear"></i>
                  <strong>내 발송 설정</strong>
                </div>

                <div class="us-row">
                  <span class="us-label">사용</span>
                  <label class="mini-toggle" :class="{ on: viewerUserSetting.enabled }" @click.stop>
                    <input type="checkbox" v-model="viewerUserSetting.enabled">
                    <span class="mini-toggle-track"><span class="mini-toggle-knob"></span></span>
                  </label>
                </div>

                <div class="us-row">
                  <span class="us-label">발송 대상</span>
                  <div class="us-action">
                    <strong>{{ selectedCustomers.length }}명</strong>
                    <button class="btn-outline-sm" @click.stop="showCustomerPicker = true">
                      <i class="ph ph-user-list"></i> 선택
                    </button>
                  </div>
                </div>

                <div class="us-row">
                  <span class="us-label">선물 첨부</span>
                  <label class="mini-toggle" :class="{ on: viewerUserSetting.attach_gift }" @click.stop>
                    <input type="checkbox" v-model="viewerUserSetting.attach_gift">
                    <span class="mini-toggle-track"><span class="mini-toggle-knob"></span></span>
                  </label>
                </div>

                <div v-if="viewerUserSetting.attach_gift" class="us-gift-detail" @click.stop>
                  <select v-model="viewerUserSetting.gift_product" class="us-select">
                    <option value="random">🎲 랜덤</option>
                    <option v-for="p in giftProducts" :key="p.goods_name" :value="p.goods_name">
                      {{ p.goods_name }} ({{ p.stock }}개)
                    </option>
                  </select>
                  <div class="us-gift-max">
                    <span>최대</span>
                    <input type="number" v-model.number="viewerUserSetting.gift_max_count" min="1">
                    <span>개</span>
                  </div>
                </div>

                <button class="us-save-btn" @click.stop="saveUserMsgSetting" :disabled="viewerUserSetting.saving">
                  <i class="ph ph-floppy-disk"></i>
                  {{ viewerUserSetting.saving ? '저장 중...' : '설정 저장' }}
                </button>
              </div>
            </div>

            <div class="sheet-actions">
              <button class="btn-ghost" @click="showMessageViewer = false">닫기</button>
              <!-- 공통 메시지 - 관리자만 원본 수정 -->
              <button 
                v-if="viewingMessage?.is_basic == 1 && isAdmin"
                class="btn-filled btn-origin-edit"
                @click="editOriginalMessage"
              >
                <i class="ph ph-warning"></i> 원본 수정
              </button>
              <!-- 내 메시지 - 수정 -->
              <button 
                v-if="viewingMessage?.is_basic != 1 && canEditMessage(viewingMessage)" 
                class="btn-filled"
                @click="editFromViewer"
              >
                <i class="ph ph-pencil-simple"></i> 수정
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ==================== 템플릿 뷰어 모달 ==================== -->
    <Teleport to="body">
      <transition name="slide-up">
        <div v-if="showTemplateViewer" class="sheet-overlay" @click.self="showTemplateViewer = false">
          <div class="bottom-sheet">
            <div class="sheet-handle"></div>
            <div class="sheet-header">
              <h3>{{ viewingTemplate?.title }}</h3>
              <button class="sheet-close" @click="showTemplateViewer = false">
                <i class="ph ph-x"></i>
              </button>
            </div>
            
            <div class="sheet-body">
              <div class="tpl-viewer-img">
                <img v-if="viewingTemplate?.image" :src="viewingTemplate.image" alt="">
                <div v-else class="tpl-viewer-placeholder" :class="viewingTemplate?.category">
                  <i :class="getCategoryIcon(viewingTemplate?.category)"></i>
                </div>
              </div>
              
              <div class="tpl-viewer-meta">
                <span class="tpl-cat-badge" :class="viewingTemplate?.category">
                  {{ getCategoryLabel(viewingTemplate?.category) }}
                </span>
                <span class="use-count-text">
                  <i class="ph ph-hash"></i> {{ viewingTemplate?.use_count || 0 }}회 사용
                </span>
              </div>
              
              <div class="tpl-viewer-text">
                <label>메시지 내용</label>
                <div class="text-block">{{ viewingTemplate?.content }}</div>
              </div>
            </div>

            <div class="sheet-actions">
              <button class="btn-ghost" @click="showTemplateViewer = false">닫기</button>
              <button class="btn-filled" @click="useTemplate(viewingTemplate)">
                <i class="ph ph-download-simple"></i> 사용하기
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ==================== 템플릿 편집 모달 ==================== -->
    <Teleport to="body">
      <transition name="slide-up">
        <div v-if="showTemplateEditor" class="fullscreen-modal-overlay">
          <div class="fullscreen-modal">
            <div class="fm-header">
              <button class="fm-back" @click="closeTemplateEditor">
                <i class="ph ph-arrow-left"></i>
              </button>
              <h3>{{ editingTemplate.idx ? '템플릿 수정' : '새 템플릿' }}</h3>
              <button class="fm-save" @click="saveTemplate" :disabled="savingTemplate">
                {{ savingTemplate ? '저장 중...' : '저장' }}
              </button>
            </div>
            
            <div class="fm-body">
              <div class="form-group">
                <label>이미지</label>
                <div class="img-upload-area" @click="triggerTemplateImageUpload">
                  <img v-if="editingTemplate.image" :src="editingTemplate.image" alt="">
                  <div v-else class="upload-empty">
                    <i class="ph ph-image"></i>
                    <span>이미지 추가</span>
                  </div>
                  <button v-if="editingTemplate.image" class="img-remove" @click.stop="editingTemplate.image = null">
                    <i class="ph ph-x"></i>
                  </button>
                </div>
                <input ref="templateImageInput" type="file" accept="image/*" @change="handleTemplateImageUpload" hidden>
              </div>

              <div class="form-group">
                <label>제목</label>
                <input type="text" v-model="editingTemplate.title" placeholder="템플릿 제목">
              </div>

              <div class="form-group">
                <label>카테고리</label>
                <select v-model="editingTemplate.category">
                  <option value="birthday">생일</option>
                  <option value="anniversary">기념일</option>
                  <option value="payment">납입</option>
                  <option value="greeting">인사</option>
                  <option value="promotion">홍보</option>
                  <option value="general">일반</option>
                </select>
              </div>

              <div class="form-group">
                <label>내용</label>
                <textarea ref="templateTextarea" v-model="editingTemplate.content" rows="5" placeholder="템플릿 내용..."></textarea>
                <div class="var-chips">
                  <span class="var-label">변수:</span>
                  <code 
                    v-for="v in availableVariables" 
                    :key="v.code" 
                    @click="insertTemplateVariable(v.code)"
                  >{{ v.label }}</code>
                </div>
              </div>

              <div class="form-group">
                <label class="toggle-row">
                  <input type="checkbox" v-model="editingTemplate.is_public">
                  <span class="toggle-text">다른 사용자에게 공개</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ==================== 팩 뷰어 (내 설정) ==================== -->
    <Teleport to="body">
      <transition name="slide-up">
        <div v-if="showPackViewer" class="sheet-overlay" @click.self="showPackViewer = false">
          <div class="bottom-sheet pack-viewer-sheet">
            <div class="sheet-handle"></div>
            <div class="sheet-header">
              <h3>
                <i class="ph-fill ph-package" style="margin-right:6px;"></i>
                {{ viewingPack?.pack_name }}
              </h3>
              <button class="sheet-close" @click="showPackViewer = false">
                <i class="ph ph-x"></i>
              </button>
            </div>

            <div class="sheet-body">
              <!-- 팩 정보 -->
              <div class="info-grid">
                <div class="info-cell">
                  <span class="info-key">트리거</span>
                  <span class="info-val">
                    <span class="trigger-pill" :class="viewingPack?.trigger_type">
                      {{ getTriggerLabel(viewingPack?.trigger_type) }}
                    </span>
                  </span>
                </div>
                <div class="info-cell">
                  <span class="info-key">발송 시점</span>
                  <span class="info-val">{{ getPackScheduleText(viewingPack || {}) }}</span>
                </div>
                <div class="info-cell">
                  <span class="info-key">메시지 수</span>
                  <span class="info-val">{{ viewingPackItems.length }}종 (랜덤 택1)</span>
                </div>
              </div>

              <!-- 팩 아이템 미리보기 -->
              <div class="pack-items-preview">
                <div class="pv-label">
                  <i class="ph ph-list-bullets"></i>
                  사용할 메시지 선택
                  <span class="pv-selected-count">{{ selectedPackItemCount }}/{{ viewingPackItems.length }}</span>
                </div>
                <div 
                  v-for="(item, i) in viewingPackItems" 
                  :key="i" 
                  class="pv-item"
                  :class="{ 'pv-inactive': !isPackItemSelected(item) }"
                  @click.stop="togglePackItem(item)"
                >
                  <label class="pv-check" @click.stop>
                    <input type="checkbox" :checked="isPackItemSelected(item)" @change="togglePackItem(item)">
                    <span class="pv-check-box" :class="{ checked: isPackItemSelected(item) }">
                      <i v-if="isPackItemSelected(item)" class="ph-bold ph-check"></i>
                    </span>
                  </label>
                  <div class="pv-content">
                    <strong v-if="item.title">{{ item.title }}</strong>
                    <p>{{ truncate(item.content, 80) }}</p>
                  </div>
                  <img v-if="item.image" :src="item.image" class="pv-thumb" alt="">
                </div>
              </div>

              <!-- ===== 내 발송 설정 ===== -->
              <div class="user-settings-section">
                <div class="us-header">
                  <i class="ph ph-gear"></i>
                  <strong>내 발송 설정</strong>
                </div>

                <div class="us-row">
                  <span class="us-label">사용</span>
                  <label class="mini-toggle" :class="{ on: viewerPackSetting.enabled }" @click.stop>
                    <input type="checkbox" v-model="viewerPackSetting.enabled">
                    <span class="mini-toggle-track"><span class="mini-toggle-knob"></span></span>
                  </label>
                </div>

                <div class="us-row">
                  <span class="us-label">발송 대상</span>
                  <div class="us-action">
                    <strong>{{ selectedCustomers.length }}명</strong>
                    <button class="btn-outline-sm" @click.stop="showCustomerPicker = true">
                      <i class="ph ph-user-list"></i> 선택
                    </button>
                  </div>
                </div>

                <div class="us-row">
                  <span class="us-label">선물 첨부</span>
                  <label class="mini-toggle" :class="{ on: viewerPackSetting.attach_gift }" @click.stop>
                    <input type="checkbox" v-model="viewerPackSetting.attach_gift">
                    <span class="mini-toggle-track"><span class="mini-toggle-knob"></span></span>
                  </label>
                </div>

                <div v-if="viewerPackSetting.attach_gift" class="us-gift-detail" @click.stop>
                  <select v-model="viewerPackSetting.gift_product" class="us-select">
                    <option value="random">🎲 랜덤</option>
                    <option v-for="p in giftProducts" :key="p.goods_name" :value="p.goods_name">
                      {{ p.goods_name }} ({{ p.stock }}개)
                    </option>
                  </select>
                  <div class="us-gift-max">
                    <span>최대</span>
                    <input type="number" v-model.number="viewerPackSetting.gift_max_count" min="1">
                    <span>개</span>
                  </div>
                </div>

                <button class="us-save-btn" @click.stop="saveUserPackSetting" :disabled="viewerPackSetting.saving">
                  <i class="ph ph-floppy-disk"></i>
                  {{ viewerPackSetting.saving ? '저장 중...' : '설정 저장' }}
                </button>
              </div>
            </div>

            <div class="sheet-actions">
              <button class="btn-ghost" @click="showPackViewer = false">닫기</button>
              <button 
                v-if="isAdmin"
                class="btn-filled btn-origin-edit"
                @click="editOriginalPack"
              >
                <i class="ph ph-warning"></i> 원본 수정
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ==================== 메시지 편집 모달 ==================== -->
    <Teleport to="body">
      <transition name="slide-up">
        <div v-if="showEditor" class="fullscreen-modal-overlay">
          <div class="fullscreen-modal">
            <div class="fm-header">
              <button class="fm-back" @click="closeMessageEditor">
                <i class="ph ph-arrow-left"></i>
              </button>
              <h3>{{ editingMessage.idx ? (editingMessage.is_basic ? '⚠️ 원본 수정' : '메시지 수정') : '새 메시지' }}</h3>
              <div class="fm-header-right">
                <button v-if="!editingMessage.is_basic" class="fm-draft" @click="saveMessage(true)">임시저장</button>
                <button class="fm-save" @click="saveMessage(false)" :disabled="saving">
                  {{ saving ? '저장 중...' : '저장' }}
                </button>
              </div>
            </div>
            
            <div class="fm-body">
              <!-- 원본 수정 경고 -->
              <div v-if="editingMessage.is_basic && editingMessage.idx" class="origin-warning-banner">
                <i class="ph ph-warning-circle"></i>
                <div>
                  <strong>원본 수정 모드</strong>
                  <p>이 메시지를 사용하는 모든 사용자에게 영향을 줍니다.</p>
                </div>
              </div>
              <div class="form-group">
                <label>이미지 (MMS)</label>
                <div class="img-upload-area" @click="triggerImageUpload">
                  <img v-if="editingMessage.image" :src="editingMessage.image" alt="">
                  <div v-else class="upload-empty">
                    <i class="ph ph-image"></i>
                    <span>이미지 추가</span>
                    <small>이미지 첨부 시 MMS로 발송</small>
                  </div>
                  <button v-if="editingMessage.image" class="img-remove" @click.stop="editingMessage.image = null">
                    <i class="ph ph-x"></i>
                  </button>
                </div>
                <input ref="imageInput" type="file" accept="image/*" @change="handleImageUpload" hidden>
              </div>

              <div class="form-group">
                <label>제목</label>
                <input type="text" v-model="editingMessage.title" placeholder="메시지 제목">
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>발송 유형</label>
                  <select v-model="editingMessage.type">
                    <option value="auto">자동 발송</option>
                    <option value="scheduled">예약 발송</option>
                  </select>
                </div>
                <div class="form-group" v-if="editingMessage.type === 'auto'">
                  <label>트리거</label>
                  <select v-model="editingMessage.trigger_type">
                    <option value="birthday">생일</option>
                    <option value="contract_anniversary">계약기념일</option>
                    <option value="payment_due">납입일</option>
                    <option value="holiday">명절/기념일</option>
                    <option value="custom">사용자 지정</option>
                  </select>
                </div>
              </div>

              <!-- 생일/계약기념일/납입일 -->
              <div class="form-row" v-if="editingMessage.type === 'auto' && ['birthday', 'contract_anniversary', 'payment_due'].includes(editingMessage.trigger_type)">
                <div class="form-group">
                  <label>발송 시점</label>
                  <div class="trigger-days-input">
                    <input type="number" v-model.number="editingMessage.trigger_days">
                    <span>일 {{ editingMessage.trigger_days >= 0 ? '후' : '전' }}</span>
                  </div>
                </div>
                <div class="form-group">
                  <label>발송 시간</label>
                  <input type="time" v-model="editingMessage.trigger_time">
                </div>
              </div>

              <!-- holiday / custom -->
              <div class="form-row" v-if="editingMessage.type === 'auto' && ['holiday', 'custom'].includes(editingMessage.trigger_type)">
                <div class="form-group">
                  <label>발송 날짜</label>
                  <input type="date" v-model="editingMessage.trigger_date">
                </div>
                <div class="form-group">
                  <label>발송 시간</label>
                  <input type="time" v-model="editingMessage.trigger_time">
                </div>
              </div>

              <!-- 예약 발송 -->
              <div class="form-group" v-if="editingMessage.type === 'scheduled'">
                <label>예약 일시</label>
                <input type="datetime-local" v-model="editingMessage.schedule_date">
              </div>

              <!-- 발신자 표시 -->
              <div class="form-group sender-block">
                <label class="toggle-row">
                  <input type="checkbox" v-model="editingMessage.use_header">
                  <i class="ph ph-identification-card"></i>
                  <span class="toggle-text">발신자 표시</span>
                </label>
                <div v-if="editingMessage.use_header" class="sender-preview">
                  <pre>{{ msgHeader }}</pre>
                  <button type="button" class="edit-sender-btn" @click="showHeaderEditor = true">
                    <i class="ph ph-pencil-simple"></i> 수정
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label>내용</label>
                <textarea ref="messageTextarea" v-model="editingMessage.content" rows="5" placeholder="메시지 내용..."></textarea>
                <div class="content-meta">
                  <span class="char-counter" :class="{ over: totalContentLength > 90 }">
                    {{ totalContentLength }}자
                  </span>
                  <span class="type-pill" :class="messageType">
                    {{ messageTypeLabel }}
                  </span>
                </div>
                <div class="var-chips">
                  <span class="var-label">변수:</span>
                  <code 
                    v-for="v in availableVariables" 
                    :key="v.code" 
                    @click="insertVariable(v.code)"
                  >{{ v.label }}</code>
                </div>
              </div>

              <!-- ===== 🎁 선물 첨부 옵션 (자동메시지, 공통 원본 수정시 숨김) ===== -->
              <div v-if="!editingMessage.is_basic" class="form-group gift-section">
                <label class="toggle-row gift-toggle">
                  <input type="checkbox" v-model="editingMessage.attach_gift">
                  <i class="ph-fill ph-gift"></i>
                  <span class="toggle-text">선물 첨부</span>
                </label>

                <transition name="slide-down">
                  <div v-if="editingMessage.attach_gift" class="gift-options">
                    <div class="gift-stock-info">
                      <i class="ph ph-package"></i>
                      <span>보유 선물: <strong>{{ giftStockTotal }}개</strong> ({{ giftProducts.length }}종)</span>
                      <button class="gift-refresh-btn" @click="loadGiftProducts" title="새로고침">
                        <i class="ph ph-arrows-clockwise"></i>
                      </button>
                    </div>

                    <div class="gift-product-select">
                      <label>상품 선택</label>
                      <select v-model="editingMessage.gift_product">
                        <option value="random">🎲 랜덤 (보유 상품 중)</option>
                        <option 
                          v-for="p in giftProducts" 
                          :key="p.goods_name"
                          :value="p.goods_name"
                        >
                          {{ p.goods_name }} ({{ p.stock }}개)
                        </option>
                      </select>
                    </div>

                    <div class="gift-max-row">
                      <label>최대 발송 수</label>
                      <div class="gift-max-control">
                        <input 
                          type="number" 
                          v-model.number="editingMessage.gift_max_count" 
                          min="1"
                        />
                        <span class="gift-max-hint">개</span>
                      </div>
                    </div>

                    <div class="gift-note">
                      <i class="ph ph-info"></i>
                      재고 없으면 선물 없이 메시지만 발송됩니다.
                    </div>
                  </div>
                </transition>
              </div>

              <!-- 공통 메시지 설정 -->
              <div v-if="isAdmin" class="form-group">
                <label class="toggle-row highlight-toggle">
                  <input type="checkbox" v-model="editingMessage.is_basic">
                  <i class="ph ph-globe"></i>
                  <span class="toggle-text">공통 메시지로 설정</span>
                </label>
                <small class="hint-text">공통 메시지로 설정하면 모든 사용자에게 표시되며, 각 사용자가 개별 발송 설정을 합니다</small>
              </div>

              <div v-if="!editingMessage.is_basic" class="form-group">
                <label>발송 대상</label>
                <div class="target-bar">
                  <span class="target-count">
                    <i class="ph ph-users"></i>
                    {{ selectedCustomers.length }}명 선택
                  </span>
                  <button type="button" @click="showCustomerPicker = true" class="btn-outline-sm">
                    <i class="ph ph-user-list"></i> 대상 선택
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ==================== 메시지 팩 에디터 ==================== -->
    <Teleport to="body">
    <transition name="slide-up">
      <div v-if="showPackEditor" class="fullscreen-modal-overlay">
        <div class="fullscreen-modal">
          <div class="fm-header">
            <button class="fm-back" @click="closePackEditor">
              <i class="ph ph-arrow-left"></i>
            </button>
            <h3>📦 {{ packSettings.idx ? (packSettings.is_basic ? '⚠️ 원본 수정' : '팩 수정') : '새 메시지 팩' }}</h3>
            <div class="fm-header-right">
              <button class="fm-save" @click="savePack" :disabled="savingPack">
                {{ savingPack ? '저장 중...' : `${packItems.length}종 저장` }}
              </button>
            </div>
          </div>
          <div class="fm-body">
            <!-- 원본 수정 경고 -->
            <div v-if="packSettings.is_basic && packSettings.idx" class="origin-warning-banner">
              <i class="ph ph-warning-circle"></i>
              <div>
                <strong>원본 수정 모드</strong>
                <p>이 팩을 사용하는 모든 사용자에게 영향을 줍니다.</p>
              </div>
            </div>
            <!-- ===== 콘텐츠 편집 ===== -->
            <div class="fm-section">
              <div class="form-group">
                <label>팩 이름</label>
                <input type="text" v-model="packSettings.name" placeholder="예: 설날인사, 생일축하">
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>트리거</label>
                  <select v-model="packSettings.trigger_type">
                    <option value="birthday">생일</option>
                    <option value="contract_anniversary">계약기념일</option>
                    <option value="payment_due">납입일</option>
                    <option value="holiday">명절/기념일</option>
                    <option value="custom">사용자 지정</option>
                  </select>
                </div>
              </div>

              <div class="form-row" v-if="['birthday', 'contract_anniversary', 'payment_due'].includes(packSettings.trigger_type)">
                <div class="form-group">
                  <label>발송 시점</label>
                  <div class="trigger-days-input">
                    <input type="number" v-model.number="packSettings.trigger_days">
                    <span>일 {{ packSettings.trigger_days >= 0 ? '후' : '전' }}</span>
                  </div>
                </div>
                <div class="form-group">
                  <label>발송 시간</label>
                  <input type="time" v-model="packSettings.trigger_time">
                </div>
              </div>

              <div class="form-row" v-if="['holiday', 'custom'].includes(packSettings.trigger_type)">
                <div class="form-group">
                  <label>발송 날짜</label>
                  <input type="date" v-model="packSettings.trigger_date">
                </div>
                <div class="form-group">
                  <label>발송 시간</label>
                  <input type="time" v-model="packSettings.trigger_time">
                </div>
              </div>

              <div class="form-group sender-block">
                <label>
                  <input type="checkbox" v-model="packSettings.use_header">
                  발신자 정보 추가
                </label>
                <label v-if="isAdmin" style="margin-left: 16px;">
                  <input type="checkbox" v-model="packSettings.is_basic">
                  공통 메시지 (원본)
                </label>
              </div>
            </div>

            <!-- ===== 발송 설정 ===== -->
            <div class="fm-section">

              <!-- 🎁 선물 첨부 옵션 -->
              <div class="form-group gift-section">
                <label class="toggle-row gift-toggle">
                  <input type="checkbox" v-model="packSettings.attach_gift">
                  <i class="ph-fill ph-gift"></i>
                  <span class="toggle-text">선물 첨부</span>
                </label>

                <transition name="slide-down">
                  <div v-if="packSettings.attach_gift" class="gift-options">
                    <div class="gift-stock-info">
                      <i class="ph ph-package"></i>
                      <span>보유 선물: <strong>{{ giftStockTotal }}개</strong> ({{ giftProducts.length }}종)</span>
                      <button class="gift-refresh-btn" @click="loadGiftProducts" title="새로고침">
                        <i class="ph ph-arrows-clockwise"></i>
                      </button>
                    </div>

                    <div class="gift-product-select">
                      <label>상품 선택</label>
                      <select v-model="packSettings.gift_product">
                        <option value="random">🎲 랜덤 (보유 상품 중)</option>
                        <option 
                          v-for="p in giftProducts" 
                          :key="'pack-gift-' + p.goods_name"
                          :value="p.goods_name"
                        >
                          {{ p.goods_name }} ({{ p.stock }}개)
                        </option>
                      </select>
                    </div>

                    <div class="gift-max-row">
                      <label>최대 발송 수</label>
                      <div class="gift-max-control">
                        <input 
                          type="number" 
                          v-model.number="packSettings.gift_max_count" 
                          min="1"
                        />
                        <span class="gift-max-hint">개</span>
                      </div>
                    </div>

                    <div class="gift-note">
                      <i class="ph ph-info"></i>
                      재고 없으면 선물 없이 메시지만 발송됩니다.
                    </div>
                  </div>
                </transition>
              </div>

              <!-- 발송 대상 -->
              <div class="form-group">
                <label>발송 대상</label>
                <div class="target-bar">
                  <span class="target-count">
                    <i class="ph ph-users"></i>
                    {{ selectedCustomers.length }}명 선택
                  </span>
                  <button type="button" @click="showCustomerPicker = true" class="btn-outline-sm">
                    <i class="ph ph-user-list"></i> 대상 선택
                  </button>
                </div>
              </div>
            </div>

            <!-- ===== 메시지 목록 편집 ===== -->
            <div class="fm-section">
              <div class="pack-items-header">
                <label>메시지 목록 ({{ packItems.length }}종)</label>
                <button class="btn-text" @click="addPackItem">
                  <i class="ph ph-plus-circle"></i> 추가
                </button>
              </div>

              <div v-for="(item, i) in packItems" :key="i" class="pack-item">
                <div class="pack-item-header">
                  <span class="pack-item-num">{{ i + 1 }}</span>
                  <input type="text" v-model="item.title" :placeholder="`메시지 ${i + 1} 제목`" class="pack-item-title">
                  <button class="pack-item-delete" @click="removePackItem(i)" v-if="packItems.length > 1">
                    <i class="ph ph-x"></i>
                  </button>
                </div>
                <textarea v-model="item.content" rows="3" :placeholder="`메시지 내용 (변수: {{name}}, {{nickname}} 등)`"></textarea>
                <div class="pack-item-footer">
                  <button class="btn-text" @click="triggerPackImageUpload(i)">
                    <i class="ph ph-image"></i> {{ item.image ? '이미지 변경' : '이미지 첨부' }}
                  </button>
                  <img v-if="item.image" :src="item.image" class="pack-item-thumb" @click="item.image = null">
                </div>
              </div>
              <input ref="packImageInput" type="file" accept="image/*" @change="handlePackImageUpload" hidden>
            </div>
          </div>
        </div>
      </div>
    </transition>
    </Teleport>

    <!-- ==================== 직접 발송 모달 ==================== -->
    <Teleport to="body">
      <transition name="slide-up">
        <div v-if="showInstantSender" class="fullscreen-modal-overlay">
          <div class="fullscreen-modal">
            <div class="fm-header">
              <button class="fm-back" @click="closeInstantSender">
                <i class="ph ph-arrow-left"></i>
              </button>
              <h3>
                <i v-if="isGroupMode" class="ph ph-users-three" style="color: #8b5cf6;"></i>
                <i v-else class="ph ph-paper-plane-tilt" style="color: #10b981;"></i>
                {{ isGroupMode ? '그룹 발송' : '직접 발송' }}
              </h3>
              <button 
                class="fm-save send-btn"
                @click="sendInstantMessage"
                :disabled="sendingInstant || instantRecipients.length === 0 || !instantMessage.content"
              >
                <i class="ph ph-paper-plane-tilt"></i>
                {{ sendingInstant ? '발송 중...' : `${instantRecipients.length}명 발송` }}
              </button>
            </div>
            
            <div class="fm-body">
              <div class="form-group">
                <label>이미지 (MMS)</label>
                <div class="img-upload-area" @click="triggerInstantImageUpload">
                  <img v-if="instantMessage.image" :src="instantMessage.image" alt="">
                  <div v-else class="upload-empty">
                    <i class="ph ph-image"></i>
                    <span>이미지 추가</span>
                    <small>이미지 첨부 시 MMS로 발송</small>
                  </div>
                  <button v-if="instantMessage.image" class="img-remove" @click.stop="instantMessage.image = null">
                    <i class="ph ph-x"></i>
                  </button>
                </div>
                <input ref="instantImageInput" type="file" accept="image/*" @change="handleInstantImageUpload" hidden>
              </div>

              <div class="form-group">
                <label>제목 (LMS/MMS 전용)</label>
                <input type="text" v-model="instantMessage.title" placeholder="메시지 제목">
              </div>

              <div class="form-group sender-block">
                <label class="toggle-row">
                  <input type="checkbox" v-model="instantMessage.use_header">
                  <i class="ph ph-identification-card"></i>
                  <span class="toggle-text">발신자 표시</span>
                </label>
                <div v-if="instantMessage.use_header" class="sender-preview">
                  <pre>{{ msgHeader }}</pre>
                  <button type="button" class="edit-sender-btn" @click="showHeaderEditor = true">
                    <i class="ph ph-pencil-simple"></i> 수정
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label>내용</label>
                <textarea 
                  ref="instantTextarea" 
                  v-model="instantMessage.content" 
                  rows="4" 
                  placeholder="메시지 내용..."
                ></textarea>
                <div class="content-meta">
                  <span class="char-counter" :class="{ over: instantTotalLength > 90 }">
                    {{ instantTotalLength }}자
                  </span>
                  <span class="type-pill" :class="instantMsgType">
                    {{ instantMsgTypeLabel }}
                  </span>
                </div>
                <div class="var-chips">
                  <span class="var-label">변수:</span>
                  <code 
                    v-for="v in availableVariables" 
                    :key="'instant-' + v.code" 
                    @click="insertInstantVariable(v.code)"
                  >{{ v.label }}</code>
                </div>
              </div>

              <!-- ===== 🎁 선물 첨부 옵션 ===== -->
              <div class="form-group gift-section">
                <label class="toggle-row gift-toggle">
                  <input type="checkbox" v-model="instantGiftEnabled">
                  <i class="ph-fill ph-gift"></i>
                  <span class="toggle-text">선물 첨부</span>
                </label>

                <transition name="slide-down">
                  <div v-if="instantGiftEnabled" class="gift-options">
                    <!-- 재고 현황 -->
                    <div class="gift-stock-info">
                      <i class="ph ph-package"></i>
                      <span>보유 선물: <strong>{{ giftStockTotal }}개</strong> ({{ giftProducts.length }}종)</span>
                      <button class="gift-refresh-btn" @click="loadGiftProducts" title="새로고침">
                        <i class="ph ph-arrows-clockwise"></i>
                      </button>
                    </div>

                    <!-- 배정 방식 선택 -->
                    <div class="gift-assign-mode">
                      <label>배정 방식</label>
                      <div class="gift-mode-tabs">
                        <button 
                          class="gift-mode-tab" 
                          :class="{ active: giftAssignMode === 'bulk' }"
                          @click="giftAssignMode = 'bulk'"
                        >
                          <i class="ph ph-users"></i> 일괄 배정
                        </button>
                        <button 
                          class="gift-mode-tab" 
                          :class="{ active: giftAssignMode === 'individual' }"
                          @click="giftAssignMode = 'individual'"
                        >
                          <i class="ph ph-user-focus"></i> 개별 지정
                        </button>
                      </div>
                    </div>

                    <!-- 일괄 배정 모드 -->
                    <template v-if="giftAssignMode === 'bulk'">
                      <div class="gift-product-select">
                        <label>상품 선택</label>
                        <select v-model="instantGiftProduct">
                          <option value="random">🎲 랜덤 (보유 상품 중)</option>
                          <option 
                            v-for="p in giftProducts" 
                            :key="p.goods_name"
                            :value="p.goods_name"
                          >
                            {{ p.goods_name }} ({{ p.stock }}개)
                          </option>
                        </select>
                      </div>

                      <div class="gift-max-row">
                        <label>최대 발송 수</label>
                        <div class="gift-max-control">
                          <input 
                            type="number" 
                            v-model.number="instantGiftMax" 
                            min="1" 
                            :max="giftStockTotal"
                          />
                          <span class="gift-max-hint">개 (재고 초과 시 메시지만 발송)</span>
                        </div>
                      </div>
                    </template>

                    <!-- 개별 지정 모드 -->
                    <template v-if="giftAssignMode === 'individual'">
                      <div class="gift-individual-section">
                        <div class="gift-individual-header">
                          <span class="gift-individual-label">
                            <i class="ph ph-list-checks"></i>
                            수신자별 선물 지정
                          </span>
                          <button class="btn-text" @click="applyGiftToAll" v-if="giftProducts.length > 0">
                            <i class="ph ph-copy"></i> 전체 일괄 적용
                          </button>
                        </div>
                        
                        <!-- 일괄 적용용 선택 -->
                        <div v-if="showBulkApply" class="gift-bulk-apply-bar">
                          <select v-model="bulkApplyProduct" class="us-select">
                            <option value="">선물 선택...</option>
                            <option value="random">🎲 랜덤</option>
                            <option v-for="p in giftProducts" :key="'ba-'+p.goods_name" :value="p.goods_name">
                              {{ p.goods_name }} ({{ p.stock }}개)
                            </option>
                          </select>
                          <button class="btn-outline-sm" @click="confirmBulkApply" :disabled="!bulkApplyProduct">적용</button>
                          <button class="btn-outline-sm cancel" @click="showBulkApply = false">취소</button>
                        </div>

                        <div v-if="instantRecipients.length === 0" class="empty-mini">
                          <i class="ph ph-user-circle"></i>
                          <p>수신자를 먼저 선택해주세요</p>
                        </div>

                        <div v-else class="gift-individual-list">
                          <div 
                            v-for="cIdx in instantRecipients" 
                            :key="'gift-assign-' + cIdx"
                            class="gift-assign-row"
                          >
                            <div class="gift-assign-person">
                              <span class="gift-assign-name">{{ getCustomerName(cIdx) }}</span>
                              <span v-if="getCustomerMType(cIdx)" class="r-mtype-badge" :class="'mtype-' + getCustomerMType(cIdx)">{{ getCustomerMType(cIdx) }}</span>
                              <span v-if="getCustomerManageRankLabel(cIdx)" class="gift-assign-grade mrank-badge">
                                {{ getCustomerManageRankLabel(cIdx) }}
                              </span>
                              <span v-if="getCustomerVipLabel(cIdx)" class="gift-assign-grade" :class="'vip-' + getCustomerVipRank(cIdx)">
                                {{ getCustomerVipLabel(cIdx) }}
                              </span>
                            </div>
                            <select 
                              class="gift-assign-select"
                              :value="giftAssignments[cIdx] || ''" 
                              @change="setGiftAssignment(cIdx, $event.target.value)"
                            >
                              <option value="">선물 없음</option>
                              <option value="random">🎲 랜덤</option>
                              <option 
                                v-for="p in giftProducts" 
                                :key="'ia-' + cIdx + '-' + p.goods_name"
                                :value="p.goods_name"
                              >
                                {{ p.goods_name }} ({{ p.stock }}개)
                              </option>
                            </select>
                          </div>
                        </div>
                        <div class="gift-individual-summary">
                          <i class="ph ph-info"></i>
                          선물 지정: <strong>{{ giftAssignedCount }}명</strong> / {{ instantRecipients.length }}명
                        </div>
                      </div>
                    </template>

                    <div class="gift-note">
                      <i class="ph ph-info"></i>
                      {{ giftAssignMode === 'individual' 
                        ? '선물 없음으로 설정된 수신자에게는 메시지만 발송됩니다.' 
                        : '수신자 수가 재고보다 많으면 선착순 발송, 나머지는 메시지만 전송됩니다.' }}
                    </div>
                  </div>
                </transition>
              </div>

              <!-- 그룹 태그 선택 (그룹 모드) -->
              <div v-if="isGroupMode" class="form-group">
                <label>
                  <i class="ph ph-funnel"></i>
                  그룹 필터
                </label>

                <!-- 관계 유형 필터 -->
                <div class="grade-filter-section">
                  <div class="grade-filter-label">
                    <i class="ph ph-handshake"></i> 관계
                  </div>
                  <div class="group-tag-list">
                    <button 
                      v-for="mt in mTypeOptions" 
                      :key="'mt-' + mt.value"
                      class="group-tag-chip"
                      :class="[{ active: selectedMTypes.includes(mt.value) }, 'mtype-chip-' + mt.value]"
                      @click="toggleMType(mt.value)"
                    >
                      <i class="ph" :class="selectedMTypes.includes(mt.value) ? 'ph-check-circle' : 'ph-circle'"></i>
                      {{ mt.label }}
                      <span class="grade-count">{{ getMTypeCount(mt.value) }}</span>
                    </button>
                  </div>
                </div>

                <!-- 관리 등급 필터 -->
                <div class="grade-filter-section">
                  <div class="grade-filter-label">
                    <i class="ph ph-steps"></i> 관리 등급
                  </div>
                  <div class="group-tag-list manage-rank-list">
                    <button 
                      v-for="opt in manageRankOptions" 
                      :key="'mrank-' + opt.value"
                      class="group-tag-chip manage-rank-chip"
                      :class="[{ active: selectedManageRanks.includes(opt.value) }, 'mrank-' + opt.value]"
                      @click="toggleManageRank(opt.value)"
                    >
                      <i class="ph" :class="selectedManageRanks.includes(opt.value) ? 'ph-check-circle' : 'ph-circle'"></i>
                      {{ opt.label }}
                      <span class="grade-count">{{ getManageRankCount(opt.value) }}</span>
                    </button>
                  </div>
                </div>

                <!-- 고객 등급 필터 -->
                <div class="grade-filter-section">
                  <div class="grade-filter-label">
                    <i class="ph ph-crown"></i> VIP 등급
                  </div>
                  <div class="group-tag-list">
                    <button 
                      v-for="grade in vipGrades" 
                      :key="'vip-' + grade.value"
                      class="group-tag-chip"
                      :class="[{ active: selectedVipGrades.includes(grade.value) }, 'vip-chip-' + grade.value]"
                      @click="toggleVipGrade(grade.value)"
                    >
                      <i class="ph" :class="selectedVipGrades.includes(grade.value) ? 'ph-check-circle' : 'ph-circle'"></i>
                      {{ grade.label }}
                      <span class="grade-count">{{ getVipGradeCount(grade.value) }}</span>
                    </button>
                  </div>
                </div>

                <div class="grade-filter-section">
                  <div class="grade-filter-label">
                    <i class="ph ph-tag"></i> 태그
                  </div>
                  <div v-if="customerTags.length > 0" class="group-tag-list">
                    <button 
                      v-for="tag in customerTags" 
                      :key="'gtag-' + tag.idx"
                      class="group-tag-chip"
                      :class="{ active: selectedGroupTags.includes(tag.name) }"
                      @click="toggleGroupTag(tag.name)"
                    >
                      <i class="ph" :class="selectedGroupTags.includes(tag.name) ? 'ph-check-circle' : 'ph-circle'"></i>
                      {{ tag.name }}
                    </button>
                  </div>
                  <div v-else class="empty-mini">
                    <i class="ph ph-tag"></i>
                    <p>등록된 태그가 없습니다</p>
                  </div>
                </div>

                <div v-if="hasAnyGroupFilter" class="group-summary">
                  <i class="ph ph-info"></i>
                  선택된 조건의 고객 <strong>{{ filteredRecipientsCustomers.length }}명</strong>에게 발송됩니다
                </div>
              </div>

              <!-- 수신자 선택 -->
              <div class="form-group">
                <label>
                  수신자 {{ isGroupMode ? '확인' : '선택' }}
                  <span class="selected-cnt">({{ instantRecipients.length }}명 선택)</span>
                </label>
                <div class="recipient-search-bar">
                  <i class="ph ph-magnifying-glass"></i>
                  <input type="text" v-model="recipientSearch" placeholder="이름 또는 전화번호 검색...">
                </div>
                
                <div class="recipient-select-all">
                  <label class="toggle-row">
                    <input 
                      type="checkbox" 
                      :checked="isAllRecipientsSelected"
                      @change="toggleAllRecipients"
                    >
                    <span class="toggle-text">전체 선택 ({{ filteredRecipientsCustomers.length }}명)</span>
                  </label>
                </div>
                
                <div class="recipient-scroll">
                  <label 
                    v-for="c in filteredRecipientsCustomers" 
                    :key="'recipient-' + c.idx"
                    class="recipient-row"
                    :class="{ checked: instantRecipients.includes(c.idx) }"
                  >
                    <input 
                      type="checkbox" 
                      :checked="instantRecipients.includes(c.idx)"
                      @change="toggleRecipient(c.idx)"
                    >
                    <span class="r-name">{{ c.name }}</span>
                    <span v-if="c.m_type" class="r-mtype-badge" :class="'mtype-' + c.m_type">{{ c.m_type }}</span>
                    <span v-if="getManageRankShort(c.manage_rank)" class="r-manage-badge" :class="'mrank-' + c.manage_rank">{{ getManageRankShort(c.manage_rank) }}</span>
                    <span v-if="c.vip_rank > 0" class="r-vip-badge" :class="'vip-' + c.vip_rank">{{ getVipLabel(c.vip_rank) }}</span>
                    <span v-if="getCustomerTags(c).length" class="r-tags">
                      <span v-for="t in getCustomerTags(c).slice(0, 2)" :key="t" class="r-tag-chip">{{ t }}</span>
                    </span>
                    <span class="r-phone">{{ formatKRPhone(c.phone) }}</span>
                  </label>
                  
                  <div v-if="filteredRecipientsCustomers.length === 0" class="empty-mini">
                    <i class="ph ph-user-circle"></i>
                    <p>{{ isGroupMode && !hasAnyGroupFilter ? '그룹 또는 등급을 선택해주세요' : '검색 결과가 없습니다' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ==================== 발송 대상 고객 선택 모달 ==================== -->
    <Teleport to="body">
      <transition name="slide-up">
        <div v-if="showCustomerPicker" class="sheet-overlay" @click.self="showCustomerPicker = false">
          <div class="bottom-sheet tall">
            <div class="sheet-handle"></div>
            <div class="sheet-header">
              <h3>발송 대상 선택</h3>
              <button class="sheet-close" @click="showCustomerPicker = false">
                <i class="ph ph-x"></i>
              </button>
            </div>
            <div class="sheet-body">
              <!-- 그룹 필터 -->
              <div class="picker-group-filter">
                <button 
                  class="group-tag-chip small"
                  :class="{ active: pickerGroupFilter === '' && pickerVipFilter === '' && pickerManageFilter === '' && pickerMTypeFilter === '' }"
                  @click="pickerGroupFilter = ''; pickerVipFilter = ''; pickerManageFilter = ''; pickerMTypeFilter = '';"
                >
                  <i class="ph" :class="(pickerGroupFilter === '' && pickerVipFilter === '' && pickerManageFilter === '' && pickerMTypeFilter === '') ? 'ph-check-circle' : 'ph-circle'"></i>
                  전체
                </button>
                <!-- 관계 유형 -->
                <button 
                  v-for="mt in mTypeOptions" 
                  :key="'pmt-' + mt.value"
                  class="group-tag-chip small"
                  :class="[{ active: pickerMTypeFilter === mt.value }, 'mtype-chip-' + mt.value]"
                  @click="pickerMTypeFilter = pickerMTypeFilter === mt.value ? '' : mt.value; pickerGroupFilter = ''; pickerVipFilter = ''; pickerManageFilter = '';"
                >
                  <i class="ph" :class="pickerMTypeFilter === mt.value ? 'ph-check-circle' : 'ph-circle'"></i>
                  {{ mt.label }}
                </button>
                <!-- 관리 등급 -->
                <button 
                  v-for="opt in manageRankOptions" 
                  :key="'pmrank-' + opt.value"
                  class="group-tag-chip small"
                  :class="[{ active: pickerManageFilter === opt.value }, 'mrank-' + opt.value]"
                  @click="pickerManageFilter = pickerManageFilter === opt.value ? '' : opt.value; pickerGroupFilter = ''; pickerVipFilter = ''; pickerMTypeFilter = '';"
                >
                  <i class="ph" :class="pickerManageFilter === opt.value ? 'ph-check-circle' : 'ph-circle'"></i>
                  {{ opt.label }}
                </button>
                <!-- VIP 등급 필터 -->
                <button 
                  v-for="grade in vipGrades" 
                  :key="'pvip-' + grade.value"
                  class="group-tag-chip small"
                  :class="[{ active: pickerVipFilter === grade.value }, 'vip-chip-' + grade.value]"
                  @click="pickerVipFilter = pickerVipFilter === grade.value ? '' : grade.value; pickerGroupFilter = ''; pickerManageFilter = ''; pickerMTypeFilter = '';"
                >
                  <i class="ph" :class="pickerVipFilter === grade.value ? 'ph-check-circle' : 'ph-circle'"></i>
                  {{ grade.label }}
                </button>
                <!-- 태그 필터 -->
                <button 
                  v-for="tag in customerTags" 
                  :key="'picker-' + tag.idx"
                  class="group-tag-chip small"
                  :class="{ active: pickerGroupFilter === tag.name }"
                  @click="pickerGroupFilter = pickerGroupFilter === tag.name ? '' : tag.name; pickerVipFilter = ''; pickerManageFilter = ''; pickerMTypeFilter = '';"
                >
                  <i class="ph" :class="pickerGroupFilter === tag.name ? 'ph-check-circle' : 'ph-circle'"></i>
                  {{ tag.name }}
                </button>
              </div>
              <!-- 검색 + 전체 선택 -->
              <div class="picker-toolbar">
                <div class="recipient-search-bar" style="flex:1">
                  <i class="ph ph-magnifying-glass"></i>
                  <input type="text" v-model="pickerSearch" placeholder="고객 검색...">
                </div>
                <label class="select-all-toggle">
                  <input type="checkbox" :checked="isAllPickerSelected" @change="toggleSelectAll">
                  <span>전체</span>
                </label>
              </div>
              <!-- 고객 리스트 -->
              <div class="recipient-scroll">
                <label 
                  v-for="c in pickerFilteredCustomers" 
                  :key="'pick-' + c.idx"
                  class="recipient-row"
                  :class="{ checked: selectedCustomers.includes(c.idx) }"
                >
                  <input 
                    type="checkbox" 
                    :checked="selectedCustomers.includes(c.idx)"
                    @change="toggleCustomerSelect(c.idx)"
                  >
                  <span class="r-name">{{ c.name }}</span>
                  <span v-if="c.m_type" class="r-mtype-badge" :class="'mtype-' + c.m_type">{{ c.m_type }}</span>
                  <span v-if="getManageRankShort(c.manage_rank)" class="r-manage-badge" :class="'mrank-' + c.manage_rank">{{ getManageRankShort(c.manage_rank) }}</span>
                  <span v-if="c.vip_rank > 0" class="r-vip-badge" :class="'vip-' + c.vip_rank">{{ getVipLabel(c.vip_rank) }}</span>
                  <span class="r-phone">{{ formatKRPhone(c.phone) }}</span>
                </label>
              </div>
            </div>
            <div class="sheet-actions">
              <span class="exclude-count-label">{{ selectedCustomers.length }}명 선택</span>
              <button class="btn-filled" @click="showCustomerPicker = false">확인</button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- ==================== 헤더 편집 모달 ==================== -->
    <Teleport to="body">
      <transition name="slide-up">
        <div v-if="showHeaderEditor" class="sheet-overlay" @click.self="showHeaderEditor = false">
          <div class="bottom-sheet">
            <div class="sheet-handle"></div>
            <div class="sheet-header">
              <h3><i class="ph ph-identification-card"></i> 발신자 표시 설정</h3>
              <button class="sheet-close" @click="showHeaderEditor = false">
                <i class="ph ph-x"></i>
              </button>
            </div>
            
            <div class="sheet-body">
              <div class="form-group">
                <label>발신자 표시 문구</label>
                <textarea v-model="editingHeader" rows="3" placeholder="발신자 표시 문구..."></textarea>
                <small class="hint-text">메시지 맨 앞에 자동으로 추가됩니다</small>
              </div>

              <div class="header-var-area">
                <span class="var-label">자동 변수:</span>
                <code @click="insertHeaderVar('{{my_name}}')">내 이름</code>
                <code @click="insertHeaderVar('{{my_phone}}')">내 전화번호</code>
              </div>

              <button type="button" class="reset-btn" @click="resetHeader">
                <i class="ph ph-arrow-counter-clockwise"></i> 기본값으로 초기화
              </button>
            </div>

            <div class="sheet-actions">
              <button class="btn-ghost" @click="showHeaderEditor = false">취소</button>
              <button class="btn-filled" @click="saveHeader" :disabled="savingHeader">
                {{ savingHeader ? '저장 중...' : '저장' }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>


<script>
import { inject, ref, reactive, computed, onMounted, onUnmounted, nextTick } from "vue"

import bmsTest from '@/components/dialogs/bmsTest.vue'

export default {
  props: {
    oth_showCrmList: Function
  },
  components: {
    bmsTest
  },
  setup() {
    const sql_query = inject('sql_query');
    const getMbid = inject('getMbid');
    const formatKRPhone = inject('formatKRPhone');
    const member_info = inject('member_info');

    // State
    const activeTab = ref('reserved');
    const searchQuery = ref('');
    const searchFocused = ref(false);
    const fabOpen = ref(false);
    const sortOption = ref('latest');
    const showEditor = ref(false);
    const showCustomerPicker = ref(false);
    const showTemplateViewer = ref(false);
    const showTemplateEditor = ref(false);
    const showMessageViewer = ref(false);
    const showHeaderEditor = ref(false);
    const viewingMessage = ref(null);
    const saving = ref(false);
    const savingTemplate = ref(false);
    const savingHeader = ref(false);
    const pickerSearch = ref('');
    const pickerGroupFilter = ref('');
    const imageInput = ref(null);
    const templateImageInput = ref(null);
    const viewingTemplate = ref(null);
    const messageTextarea = ref(null);
    const templateTextarea = ref(null);
    const isAdmin = ref(false);

    // 유저별 공통메시지 설정
    const userMsgSettings = ref({});  // { msg_idx: { idx, enabled, target_filter, ... } }
    const viewerUserSetting = reactive({
      msg_idx: null,
      settingIdx: null,
      enabled: true,
      target_filter: [],
      target_count: 0,
      attach_gift: false,
      gift_product: 'random',
      gift_max_count: 10,
      saving: false,
    });

    // 유저별 공통팩 설정
    const userPackSettings = ref({});
    const showPackViewer = ref(false);
    const viewingPack = ref(null);
    const viewingPackItems = ref([]);
    const viewerPackSetting = reactive({
      pack_idx: null,
      settingIdx: null,
      enabled: true,
      target_filter: [],
      target_count: 0,
      attach_gift: false,
      gift_product: 'random',
      gift_max_count: 10,
      selected_items: [],  // 선택된 아이템 idx 배열
      saving: false,
    });

    // 자동발송 ON/OFF
    const autoEnabled = ref(false);

    // 헤더 관련
    const msgHeader = ref('');
    const editingHeader = ref('');
    const defaultHeader = computed(() => {
      const name = member_info?.mb_name || member_info?.name || '담당자';
      return `☂️보 다™☂️ \n👔 ${name}이 보내셨습니다.\n`;
    });

    // 직접 발송 관련 State
    const showInstantSender = ref(false);
    const showBmsTest = ref(false);
    const sendingInstant = ref(false);
    const recipientSearch = ref('');
    const instantRecipients = ref([]);
    const instantImageInput = ref(null);
    const instantTextarea = ref(null);
    const instantMessage = reactive({
      title: '',
      content: '',
      image: null,
      use_header: true
    });

    // 그룹(태그) 관련 State
    const customerTags = ref([]);
    const selectedGroupTags = ref([]);
    const isGroupMode = ref(false);
    // 자동 메시지용 (legacy)

    // 🎁 선물 첨부 관련 State
    const instantGiftEnabled = ref(false);
    const instantGiftProduct = ref('random');
    const instantGiftMax = ref(10);
    const giftProducts = ref([]);
    const giftStockTotal = computed(() => giftProducts.value.reduce((sum, p) => sum + p.stock, 0));

    // 🎁 개별 선물 지정 관련 State
    const giftAssignMode = ref('bulk');  // 'bulk' | 'individual'
    const giftAssignments = ref({});     // { customerIdx: 'gift_product_name' }
    const showBulkApply = ref(false);
    const bulkApplyProduct = ref('');

    const giftAssignedCount = computed(() => {
      return Object.values(giftAssignments.value).filter(v => v && v !== '').length;
    });

    function setGiftAssignment(customerIdx, product) {
      giftAssignments.value = { ...giftAssignments.value, [customerIdx]: product };
    }

    function applyGiftToAll() {
      showBulkApply.value = true;
    }

    function confirmBulkApply() {
      if (!bulkApplyProduct.value) return;
      const newAssignments = {};
      instantRecipients.value.forEach(idx => {
        newAssignments[idx] = bulkApplyProduct.value;
      });
      giftAssignments.value = newAssignments;
      showBulkApply.value = false;
      bulkApplyProduct.value = '';
    }

    function getCustomerName(idx) {
      const c = customers.value.find(c => c.idx === idx);
      return c?.name || '알 수 없음';
    }

    function getCustomerVipRank(idx) {
      const c = customers.value.find(c => c.idx === idx);
      return c?.vip_rank || 0;
    }

    function getCustomerVipLabel(idx) {
      const rank = getCustomerVipRank(idx);
      return getVipLabel(rank);
    }

    function getCustomerManageRankLabel(idx) {
      const c = customers.value.find(c => c.idx === idx);
      return getManageRankShort(c?.manage_rank);
    }

    function getCustomerMType(idx) {
      const c = customers.value.find(c => c.idx === idx);
      return c?.m_type || '';
    }

    // 고객 등급 관련 State & 함수
    const selectedVipGrades = ref([]);
    const selectedManageRanks = ref([]);
    const selectedMTypes = ref([]);
    const pickerVipFilter = ref('');
    const pickerManageFilter = ref('');
    const pickerMTypeFilter = ref('');

    const vipGrades = [
      { value: 1, label: '⭐ VIP' },
      { value: 2, label: '⭐⭐ VVIP' },
      { value: 3, label: '⭐⭐⭐ SVIP' },
    ];

    const manageRankOptions = [
      { value: '1', label: 'PB (홍보)' },
      { value: '2', label: 'PS (가망)' },
      { value: '3', label: 'TA (연락)' },
      { value: '4', label: 'AP (접근)' },
      { value: '5', label: 'FF (설계)' },
      { value: '6', label: 'PC (체결)' },
      { value: '7', label: 'CS (관리)' },
    ];

    const mTypeOptions = [
      { value: 'X', label: 'X 지인', color: '#3b82f6' },
      { value: 'Y', label: 'Y 인맥', color: '#f59e0b' },
      { value: 'Z', label: 'Z 개척', color: '#10b981' },
    ];

    function getVipLabel(rank) {
      if (!rank || rank == 0) return '';
      const grade = vipGrades.find(g => g.value == rank);
      return grade?.label || `VIP ${rank}`;
    }

    function getManageRankLabel(value) {
      if (!value || value == '0' || value == 0) return '';
      const v = String(value);
      const opt = manageRankOptions.find(o => o.value === v);
      return opt?.label || '';
    }

    // 뱃지용 짧은 라벨 (PB, PS, TA 등)
    function getManageRankShort(value) {
      if (!value || value == '0' || value == 0) return '';
      const v = String(value);
      const opt = manageRankOptions.find(o => o.value === v);
      if (!opt) return '';
      return opt.label.split(' ')[0]; // "PB (홍보)" → "PB"
    }

    function getMTypeLabel(value) {
      if (!value) return '';
      const opt = mTypeOptions.find(o => o.value === value);
      return opt?.label || value;
    }

    // 실제 존재하는 관리등급만 표시
    const activeManageRanks = computed(() => {
      return manageRankOptions.filter(opt => 
        customers.value.some(c => String(c.manage_rank) === opt.value)
      );
    });

    // 실제 존재하는 관계유형만 표시
    const activeMTypes = computed(() => {
      return mTypeOptions.filter(opt =>
        customers.value.some(c => c.m_type === opt.value)
      );
    });

    function getVipGradeCount(value) {
      return customers.value.filter(c => Number(c.vip_rank) === Number(value)).length;
    }

    function getManageRankCount(value) {
      return customers.value.filter(c => String(c.manage_rank) === String(value)).length;
    }

    function getMTypeCount(value) {
      return customers.value.filter(c => c.m_type === value).length;
    }

    function toggleVipGrade(value) {
      const idx = selectedVipGrades.value.indexOf(value);
      if (idx >= 0) selectedVipGrades.value.splice(idx, 1);
      else selectedVipGrades.value.push(value);
      if (isGroupMode.value) autoSelectByFilters();
    }

    function toggleManageRank(rank) {
      const idx = selectedManageRanks.value.indexOf(rank);
      if (idx >= 0) selectedManageRanks.value.splice(idx, 1);
      else selectedManageRanks.value.push(rank);
      if (isGroupMode.value) autoSelectByFilters();
    }

    function toggleMType(type) {
      const idx = selectedMTypes.value.indexOf(type);
      if (idx >= 0) selectedMTypes.value.splice(idx, 1);
      else selectedMTypes.value.push(type);
      if (isGroupMode.value) autoSelectByFilters();
    }

    const hasAnyGroupFilter = computed(() => {
      return selectedGroupTags.value.length > 0 || selectedVipGrades.value.length > 0 || selectedManageRanks.value.length > 0 || selectedMTypes.value.length > 0;
    });

    // 🎁 보유 선물 상품 목록 조회
    async function loadGiftProducts() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT memo, COUNT(*) as stock
        FROM giftcoupon
        WHERE issued_by = '${mbid}'
          AND status IN ('발행', '사용대기')
        GROUP BY memo
      `);
      if (!Array.isArray(rows)) { giftProducts.value = []; return; }

      giftProducts.value = rows.map(r => {
        let memoData = {};
        try { memoData = typeof r.memo === 'string' ? JSON.parse(r.memo) : r.memo; } catch(e) { console.log(e) }
        return {
          goods_name: memoData.goods_name || '알 수 없음',
          goods_img: memoData.goods_img || '',
          brand_name: memoData.brand_name || '',
          stock: r.stock
        };
      }).filter(p => p.goods_name !== '알 수 없음');
    }

    // 🎁 선물 링크 생성 (단건)
    function generateGiftToken() {
      return Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    }

    async function createGiftLink(customer, productFilter) {
      const mbid = getMbid();

      // 쿠폰 조회 (productFilter === 'random'이면 아무거나, 아니면 해당 상품만)
      let couponSql = `
        SELECT idx, coupon_code, ktgift_product_code, amount, memo
        FROM giftcoupon
        WHERE issued_by = '${mbid}'
          AND status IN ('발행', '사용대기')
      `;
      if (productFilter && productFilter !== 'random') {
        const escaped = productFilter.replace(/'/g, "\\'");
        couponSql += ` AND memo LIKE '%${escaped}%'`;
      }
      if (productFilter === 'random') {
        couponSql += ` ORDER BY RAND()`;
      }
      couponSql += ` LIMIT 1`;

      const coupons = await sql_query(couponSql);
      if (!Array.isArray(coupons) || coupons.length === 0) return null;

      const coupon = coupons[0];
      let memoData = {};
      try { memoData = typeof coupon.memo === 'string' ? JSON.parse(coupon.memo) : coupon.memo; } catch(e) { console.log(e) }

      const token = generateGiftToken();

      // gift_links 생성
      await sql_query(`
        INSERT INTO gift_links (
          token, coupon_idx, goods_code, sender_id,
          receiver_phone, receiver_name, gift_message,
          goods_name, brand_name, brand_img, goods_img,
          sale_price, discount_price, status, created_at, expires_at
        ) VALUES (
          '${token}', ${coupon.idx},
          '${coupon.ktgift_product_code || memoData.goods_code || ''}',
          '${mbid}',
          '${(customer.phone || '').replace(/-/g, '')}',
          '${(customer.name || '').replace(/'/g, "\\'")}',
          'CRM 자동 선물',
          '${(memoData.goods_name || '').replace(/'/g, "\\'")}',
          '${(memoData.brand_name || '').replace(/'/g, "\\'")}',
          '${memoData.brand_img || ''}',
          '${memoData.goods_img || ''}',
          ${memoData.sale_price || coupon.amount || 0},
          ${memoData.discount_price || coupon.amount || 0},
          'pending', NOW(), DATE_ADD(NOW(), INTERVAL 7 DAY)
        )
      `);

      // 쿠폰 상태 변경
      await sql_query(`
        UPDATE giftcoupon 
        SET status = '제공',
            issued_to = '${(customer.name || '').replace(/'/g, "\\'")}(${(customer.phone || '').replace(/-/g, '')})'
        WHERE idx = ${coupon.idx}
      `);

      return `${window.location.origin}/gift/${token}`;
    }

    // 메뉴 상태 (Portal 방식)
    const menuState = reactive({
      visible: false,
      x: 0,
      y: 0,
      item: null,
      type: null
    });

    const tabs = [
      { id: 'reserved', name: '예약 메시지', icon: 'ph ph-package' },
      { id: 'personal', name: '개인 메시지', icon: 'ph ph-chat-circle-dots' },
      { id: 'templates', name: '문자 예제', icon: 'ph ph-file-text' },
      { id: 'history', name: '발송이력', icon: 'ph ph-clock-counter-clockwise' }
    ];

    // Data
    const messages = ref([]);
    const templates = ref([]);
    const historyLogs = ref([]);
    const customers = ref([]);
    const excludeList = ref([]); // legacy (서버 호환)
    const selectedCustomers = ref([]);

    // Editing Message
    const editingMessage = reactive({
      idx: null,
      title: '',
      content: '',
      image: null,
      type: 'auto',
      trigger_type: 'birthday',
      trigger_days: 0,
      trigger_date: '',
      trigger_time: '09:00',
      schedule_date: '',
      status: 'active',
      is_basic: false,
      use_header: true,
      attach_gift: false,
      gift_product: 'random',
      gift_max_count: 10,
    });

    // 팩 관련
    const existingPacks = ref([]);
    // 팩 에디터
    const showPackEditor = ref(false);
    const savingPack = ref(false);
    const packImageInput = ref(null);
    const packImageTargetIdx = ref(null);

    // 메시지 선택 모드 (미사용)
    const packSettings = reactive({
      idx: null,
      name: '',
      trigger_type: 'custom',
      trigger_days: 0,
      trigger_date: '',
      trigger_time: '09:00',
      use_header: true,
      is_basic: false,
      attach_gift: false,
      gift_product: 'random',
      gift_max_count: 10
    });
    const packItems = ref([
      { title: '', content: '', image: null },
      { title: '', content: '', image: null }
    ]);

    // Editing Template
    const editingTemplate = reactive({
      idx: null,
      title: '',
      content: '',
      image: null,
      category: 'general',
      is_public: false
    });

    const availableVariables = [
      { code: '{{name}}', label: '이름' },
      { code: '{{nickname}}', label: '별명' },
      { code: '{{title}}', label: '호칭' },
      { code: '{{phone}}', label: '전화번호' },
      { code: '{{birth}}', label: '생년월일' },
    ];

    // Computed
    const getSearchPlaceholder = computed(() => {
      const map = {
        reserved: '메시지 팩 검색...',
        personal: '개인 메시지 검색...',
        templates: '템플릿 검색...',
        history: '이력 검색...'
      };
      return map[activeTab.value] || '검색...';
    });

    const commonMessages = computed(() => {
      let list = messages.value.filter(m => m.is_basic === 1 || m.is_basic === '1');
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(m => 
          m.title?.toLowerCase().includes(q) || 
          m.content?.toLowerCase().includes(q)
        );
      }
      return sortList(list, 'created_at');
    });

    const myMessages = computed(() => {
      const mbid = getMbid();
      let list = messages.value.filter(m => 
        (!m.is_basic || m.is_basic === 0 || m.is_basic === '0') && 
        m.mb_id === mbid
      );
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(m => 
          m.title?.toLowerCase().includes(q) || 
          m.content?.toLowerCase().includes(q)
        );
      }
      return sortList(list, 'created_at');
    });

    const filteredTemplates = computed(() => {
      let list = templates.value;
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(t => 
          t.title?.toLowerCase().includes(q) || 
          t.content?.toLowerCase().includes(q)
        );
      }
      return list;
    });

    const filteredHistory = computed(() => {
      let list = historyLogs.value;
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(h => 
          h.customer_name?.toLowerCase().includes(q) ||
          h.title?.toLowerCase().includes(q)
        );
      }
      return sortList(list, 'sent_at');
    });

    const pickerFilteredCustomers = computed(() => {
      let list = customers.value.filter(c => c.phone);
      if (pickerGroupFilter.value) {
        list = list.filter(c => {
          let tags = [];
          try { tags = typeof c.tags === 'string' ? JSON.parse(c.tags) : (c.tags || []); } catch { tags = []; }
          return tags.includes(pickerGroupFilter.value);
        });
      }
      if (pickerVipFilter.value !== '') {
        list = list.filter(c => Number(c.vip_rank) === Number(pickerVipFilter.value));
      }
      if (pickerManageFilter.value) {
        list = list.filter(c => String(c.manage_rank) === pickerManageFilter.value);
      }
      if (pickerMTypeFilter.value) {
        list = list.filter(c => c.m_type === pickerMTypeFilter.value);
      }
      if (pickerSearch.value) {
        const q = pickerSearch.value.toLowerCase();
        list = list.filter(c => 
          c.name?.toLowerCase().includes(q) ||
          c.phone?.includes(q)
        );
      }
      return list;
    });

    const isAllPickerSelected = computed(() => {
      const filtered = pickerFilteredCustomers.value;
      return filtered.length > 0 && filtered.every(c => selectedCustomers.value.includes(c.idx));
    });

    function toggleCustomerSelect(idx) {
      const i = selectedCustomers.value.indexOf(idx);
      if (i >= 0) selectedCustomers.value.splice(i, 1);
      else selectedCustomers.value.push(idx);
    }

    function toggleSelectAll() {
      const filtered = pickerFilteredCustomers.value;
      if (isAllPickerSelected.value) {
        // 현재 필터된 것만 해제
        const ids = filtered.map(c => c.idx);
        selectedCustomers.value = selectedCustomers.value.filter(id => !ids.includes(id));
      } else {
        // 현재 필터된 것 전부 추가
        const existing = new Set(selectedCustomers.value);
        filtered.forEach(c => existing.add(c.idx));
        selectedCustomers.value = [...existing];
      }
    }

    const filteredRecipientsCustomers = computed(() => {
      let list = customers.value;
      // 그룹 태그 필터
      if (selectedGroupTags.value.length > 0) {
        list = list.filter(c => {
          let tags = [];
          try {
            tags = typeof c.tags === 'string' ? JSON.parse(c.tags) : (c.tags || []);
          } catch { tags = []; }
          return selectedGroupTags.value.some(t => tags.includes(t));
        });
      }
      // VIP 등급 필터
      if (selectedVipGrades.value.length > 0) {
        list = list.filter(c => selectedVipGrades.value.includes(Number(c.vip_rank)));
      }
      // 관리 등급 필터
      if (selectedManageRanks.value.length > 0) {
        list = list.filter(c => selectedManageRanks.value.includes(String(c.manage_rank)));
      }
      // 관계 유형 필터
      if (selectedMTypes.value.length > 0) {
        list = list.filter(c => selectedMTypes.value.includes(c.m_type));
      }
      // 텍스트 검색
      if (recipientSearch.value) {
        const q = recipientSearch.value.toLowerCase();
        list = list.filter(c => 
          c.name?.toLowerCase().includes(q) ||
          c.phone?.includes(q)
        );
      }
      return list;
    });

    const isAllRecipientsSelected = computed(() => {
      if (filteredRecipientsCustomers.value.length === 0) return false;
      return filteredRecipientsCustomers.value.every(c => 
        instantRecipients.value.includes(c.idx)
      );
    });

    const headerLength = computed(() => {
      return msgHeader.value?.length || 0;
    });

    const contentLength = computed(() => {
      return editingMessage.content?.length || 0;
    });

    const totalContentLength = computed(() => {
      let len = contentLength.value;
      if (editingMessage.use_header) {
        len += headerLength.value;
      }
      return len;
    });

    const messageType = computed(() => {
      if (editingMessage.image) return 'mms';
      if (totalContentLength.value > 90) return 'lms';
      return 'sms';
    });

    const messageTypeLabel = computed(() => {
      const map = { sms: 'SMS', lms: 'LMS', mms: 'MMS' };
      return map[messageType.value];
    });

    const instantContentLength = computed(() => {
      return instantMessage.content?.length || 0;
    });

    const instantTotalLength = computed(() => {
      let len = instantContentLength.value;
      if (instantMessage.use_header) {
        len += headerLength.value;
      }
      return len;
    });

    const instantMsgType = computed(() => {
      if (instantMessage.image) return 'mms';
      if (instantTotalLength.value > 90) return 'lms';
      return 'sms';
    });

    const instantMsgTypeLabel = computed(() => {
      const map = { sms: 'SMS', lms: 'LMS', mms: 'MMS' };
      return map[instantMsgType.value];
    });

    const targetCustomerCount = computed(() => selectedCustomers.value.length);

    const menuStyle = computed(() => ({
      position: 'fixed',
      top: `${menuState.y}px`,
      left: `${menuState.x}px`
    }));

    // Methods
    function sortList(list, dateField) {
      const sorted = [...list];
      switch (sortOption.value) {
        case 'latest':
          return sorted.sort((a, b) => new Date(b[dateField]) - new Date(a[dateField]));
        case 'oldest':
          return sorted.sort((a, b) => new Date(a[dateField]) - new Date(b[dateField]));
        case 'name':
          return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        default:
          return sorted;
      }
    }

    function truncate(str, len) {
      if (!str) return '';
      return str.length > len ? str.slice(0, len) + '...' : str;
    }

    function formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }

    function getScheduleText(msg) {
      if (msg.type === 'scheduled' && msg.schedule_date) {
        return new Date(msg.schedule_date).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      }
      if (['holiday', 'custom'].includes(msg.trigger_type) && msg.trigger_date) {
        return new Date(msg.trigger_date).toLocaleDateString('ko-KR');
      }
      const days = msg.trigger_days || 0;
      const time = msg.trigger_time?.slice(0, 5) || '09:00';
      const dayText = days === 0 ? '당일' : days > 0 ? `+${days}일` : `${days}일`;
      return `${getTriggerLabel(msg.trigger_type)} ${dayText} ${time}`;
    }

    function getTriggerLabel(type) {
      const map = { 
        birthday: '생일', 
        contract_anniversary: '계약기념일', 
        payment_due: '납입일',
        holiday: '명절',
        custom: '지정일',
        instant: '즉시'
      };
      return map[type] || '-';
    }

    function getPackScheduleText(pack) {
      if (['holiday', 'custom'].includes(pack.trigger_type) && pack.trigger_date) {
        const time = pack.trigger_time?.slice(0, 5) || '09:00';
        return new Date(pack.trigger_date).toLocaleDateString('ko-KR') + ' ' + time;
      }
      const days = pack.trigger_days || 0;
      const time = pack.trigger_time?.slice(0, 5) || '09:00';
      const dayText = days === 0 ? '당일' : days > 0 ? `+${days}일` : `${days}일`;
      return `${getTriggerLabel(pack.trigger_type)} ${dayText} ${time}`;
    }

    function getTriggerIcon(type) {
      const map = {
        birthday: 'ph ph-cake',
        contract_anniversary: 'ph ph-calendar-check',
        payment_due: 'ph ph-wallet',
        holiday: 'ph ph-gift',
        custom: 'ph ph-calendar-blank'
      };
      return map[type] || 'ph ph-chat-circle-dots';
    }

    function getCategoryLabel(cat) {
      const map = { 
        birthday: '생일', 
        anniversary: '기념일', 
        payment: '납입', 
        greeting: '인사', 
        promotion: '홍보', 
        general: '일반' 
      };
      return map[cat] || '일반';
    }

    function getCategoryIcon(cat) {
      const map = {
        birthday: 'ph ph-cake',
        anniversary: 'ph ph-calendar-heart',
        payment: 'ph ph-wallet',
        greeting: 'ph ph-hand-waving',
        promotion: 'ph ph-megaphone',
        general: 'ph ph-chat-circle-text'
      };
      return map[cat] || 'ph ph-chat-circle-text';
    }

    function getSendTypeLabel(type) {
      const map = { auto: '자동', scheduled: '예약', instant: '즉시' };
      return map[type] || type || '즉시';
    }

    function getStatusLabel(status) {
      const map = { pending: '대기', success: '성공', failed: '실패' };
      return map[status] || status || '-';
    }

    function getMessageTypeFromMsg(msg) {
      if (!msg) return 'sms';
      if (msg.image) return 'mms';
      const len = getFullMessageLength(msg);
      if (len > 90) return 'lms';
      return 'sms';
    }

    function getMessageTypeLabel(msg) {
      const type = getMessageTypeFromMsg(msg);
      const map = { sms: 'SMS', lms: 'LMS', mms: 'MMS' };
      return map[type];
    }

    function getFullMessageLength(msg) {
      if (!msg) return 0;
      let len = msg.content?.length || 0;
      if (msg.use_header) {
        len += headerLength.value;
      }
      return len;
    }

    function canEditMessage(msg) {
      if (!msg) return false;
      if (msg.is_basic == 1) {
        return isAdmin.value;
      }
      return msg.mb_id === getMbid();
    }

    function editFromViewer() {
      const msg = viewingMessage.value;
      showMessageViewer.value = false;
      openMessageEditor(msg);
    }

    function closeMessageEditor() {
      if (!confirm('편집을 취소하시겠습니까?\n변경사항이 저장되지 않습니다.')) return;
      showEditor.value = false;
    }

    function closeTemplateEditor() {
      if (!confirm('편집을 취소하시겠습니까?\n변경사항이 저장되지 않습니다.')) return;
      showTemplateEditor.value = false;
    }

    // 팩 관련 함수들
    async function loadPacks() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT p.*, COUNT(i.idx) as item_count 
        FROM crm_message_packs p
        LEFT JOIN crm_pack_items i ON i.pack_idx = p.idx
        WHERE (p.mb_id = '${mbid}' OR p.is_basic = 1)
        GROUP BY p.idx
        ORDER BY p.created_at DESC
      `);
      existingPacks.value = Array.isArray(rows) ? rows : [];
      await loadUserPackSettings();
    }

    // (팩은 별도 테이블로 관리)

    function openPackEditor(existingPack = null) {
      fabOpen.value = false;
      if (existingPack) {
        // 팩 콘텐츠 설정 (공용팩은 admin만 수정 가능)
        Object.assign(packSettings, {
          idx: existingPack.idx,
          name: existingPack.pack_name,
          trigger_type: existingPack.trigger_type || 'custom',
          trigger_days: existingPack.trigger_days || 0,
          trigger_date: existingPack.trigger_date || '',
          trigger_time: existingPack.trigger_time?.slice(0, 5) || '09:00',
          use_header: existingPack.use_header == null ? true : existingPack.use_header == 1,
          is_basic: existingPack.is_basic == 1,
        });

        // 발송 설정 (loadPacks에서 공용팩은 이미 유저설정으로 오버라이드)
        packSettings.attach_gift = existingPack.attach_gift == 1;
        packSettings.gift_product = existingPack.gift_product || 'random';
        packSettings.gift_max_count = existingPack.gift_max_count || 10;

        let selected = [];
        try { selected = JSON.parse(existingPack.target_filter || '[]'); if (!Array.isArray(selected)) selected = []; } catch { selected = []; }
        selectedCustomers.value = selected;

        loadPackItems(existingPack.idx);
      } else {
        Object.assign(packSettings, {
          idx: null,
          name: '',
          trigger_type: 'custom',
          trigger_days: 0,
          trigger_date: '',
          trigger_time: '09:00',
          use_header: true,
          is_basic: false,
          attach_gift: false,
          gift_product: 'random',
          gift_max_count: 10
        });
        selectedCustomers.value = [];
        packItems.value = [
          { title: '', content: '', image: null },
          { title: '', content: '', image: null }
        ];
      }
      showPackEditor.value = true;
    }

    async function loadPackItems(packIdx) {
      const rows = await sql_query(`
        SELECT * FROM crm_pack_items WHERE pack_idx = ${packIdx} ORDER BY sort_order, idx
      `);
      if (Array.isArray(rows) && rows.length) {
        packItems.value = rows.map(r => ({
          title: r.title || '',
          content: r.content || '',
          image: r.image || null
        }));
      } else {
        packItems.value = [{ title: '', content: '', image: null }];
      }
    }

    async function deletePack(pack) {
      if (!confirm(`"${pack.pack_name}" 팩을 삭제하시겠습니까?\n소속 메시지 ${pack.item_count || 0}개도 함께 삭제됩니다.`)) return;
      await sql_query(`DELETE FROM crm_pack_items WHERE pack_idx = ${pack.idx}`);
      await sql_query(`DELETE FROM crm_message_packs WHERE idx = ${pack.idx}`);
      loadPacks();
    }

    function closePackEditor() {
      if (packItems.value.some(i => i.title || i.content)) {
        if (!confirm('팩 편집을 취소하시겠습니까?')) return;
      }
      showPackEditor.value = false;
    }

    function addPackItem() {
      packItems.value.push({ title: '', content: '', image: null });
    }

    function removePackItem(idx) {
      packItems.value.splice(idx, 1);
    }

    function triggerPackImageUpload(idx) {
      packImageTargetIdx.value = idx;
      packImageInput.value?.click();
    }

    async function handlePackImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      const idx = packImageTargetIdx.value;
      
      // 즉시 미리보기
      packItems.value[idx].image = URL.createObjectURL(file);

      const formData = new FormData();
      formData.append('idx', '');
      formData.append('table', 'crm_pack_items');
      formData.append('usage', 'crm_image');
      formData.append('user', getMbid());
      formData.append('file', file);
      
      try {
        const res = await fetch('/api/upload/image', { method: 'POST', body: formData });
        const data = await res.json();
        if (data.url && packItems.value[idx]) {
          packItems.value[idx].image = data.url;
        } else {
          alert('이미지 업로드 실패');
          packItems.value[idx].image = null;
        }
      } catch (err) {
        console.error('이미지 업로드 실패:', err);
        packItems.value[idx].image = null;
      }
      e.target.value = '';
    }

    async function savePack() {
      if (!packSettings.name.trim()) { alert('팩 이름을 입력해주세요.'); return; }
      const validItems = packItems.value.filter(i => i.content.trim());
      if (validItems.length < 2) { alert('최소 2개 이상의 메시지를 작성해주세요.'); return; }

      savingPack.value = true;
      const mbid = getMbid();
      const targetCount = selectedCustomers.value.length;
      const selectedJson = JSON.stringify(selectedCustomers.value);

      const escapeSQL = (str) => {
        if (!str) return '';
        return str.replace(/\\/g, '\\\\').replace(/'/g, "''").replace(/\n/g, '\\n').replace(/\r/g, '\\r');
      };

      const formatDateFn = (dateStr) => {
        if (!dateStr) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        return dateStr.slice(0, 10);
      };

      try {
        let packIdx = packSettings.idx;
        const triggerDate = formatDateFn(packSettings.trigger_date);
        const packName = escapeSQL(packSettings.name.trim());

        if (packIdx) {
          await sql_query(`
            UPDATE crm_message_packs SET
              pack_name = '${packName}',
              trigger_type = '${packSettings.trigger_type}',
              trigger_days = ${packSettings.trigger_days || 0},
              trigger_date = ${triggerDate ? `'${triggerDate}'` : 'NULL'},
              trigger_time = '${packSettings.trigger_time || '09:00'}',
              use_header = ${packSettings.use_header ? 1 : 0},
              is_basic = ${packSettings.is_basic ? 1 : 0},
              attach_gift = ${packSettings.attach_gift ? 1 : 0},
              gift_product = ${packSettings.attach_gift ? `'${packSettings.gift_product || 'random'}'` : 'NULL'},
              gift_max_count = ${packSettings.gift_max_count || 10},
              target_count = ${targetCount},
              target_filter = '${selectedJson}',
              status = 'active'
            WHERE idx = ${packIdx}
          `);
          await sql_query(`DELETE FROM crm_pack_items WHERE pack_idx = ${packIdx}`);
        } else {
          const result = await sql_query(`
            INSERT INTO crm_message_packs 
            (mb_id, pack_name, trigger_type, trigger_days, trigger_date, trigger_time, use_header, status, is_basic, attach_gift, gift_product, gift_max_count, target_count, target_filter)
            VALUES (
              '${mbid}', '${packName}', '${packSettings.trigger_type}',
              ${packSettings.trigger_days || 0},
              ${triggerDate ? `'${triggerDate}'` : 'NULL'},
              '${packSettings.trigger_time || '09:00'}',
              ${packSettings.use_header ? 1 : 0}, 'active',
              ${packSettings.is_basic ? 1 : 0},
              ${packSettings.attach_gift ? 1 : 0},
              ${packSettings.attach_gift ? `'${packSettings.gift_product || 'random'}'` : 'NULL'},
              ${packSettings.gift_max_count || 10},
              ${targetCount}, '${selectedJson}'
            )
          `);
          packIdx = result?.insertId || result?.[0]?.insertId;
          if (!packIdx) {
            const lastRow = await sql_query(`SELECT MAX(idx) as id FROM crm_message_packs WHERE mb_id = '${mbid}'`);
            packIdx = lastRow?.[0]?.id;
          }
        }

        for (let i = 0; i < validItems.length; i++) {
          const item = validItems[i];
          const title = escapeSQL(item.title?.trim() || `${packSettings.name.trim()} #${i + 1}`);
          await sql_query(`
            INSERT INTO crm_pack_items (pack_idx, title, content, image, sort_order)
            VALUES (${packIdx}, '${title}', '${escapeSQL(item.content)}', 
              ${item.image ? `'${item.image}'` : 'NULL'}, ${i})
          `);
        }

        showPackEditor.value = false;
        loadMessages();
        loadPacks();
        alert(`📦 "${packSettings.name.trim()}" 팩 저장 완료! (${validItems.length}종)`);
      } catch (e) {
        console.error('팩 저장 오류:', e);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        savingPack.value = false;
      }
    }

    // 자동발송 ON/OFF 함수
    async function loadAutoEnabled() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT auto_enabled FROM crm_settings WHERE mb_id = '${mbid}' LIMIT 1
      `);
      if (Array.isArray(rows) && rows.length > 0) {
        autoEnabled.value = rows[0].auto_enabled == 1;
      } else {
        autoEnabled.value = false;
      }
    }

    async function toggleAutoSend() {
      const mbid = getMbid();
      const newVal = autoEnabled.value ? 0 : 1;
      const label = newVal ? 'ON' : 'OFF';
      
      if (!newVal && !confirm('자동발송을 끄시겠습니까?\n설정된 자동 메시지가 발송되지 않습니다.')) return;
      
      try {
        const existing = await sql_query(`SELECT idx FROM crm_settings WHERE mb_id = '${mbid}' LIMIT 1`);
        if (Array.isArray(existing) && existing.length > 0) {
          await sql_query(`UPDATE crm_settings SET auto_enabled = ${newVal} WHERE mb_id = '${mbid}'`);
        } else {
          await sql_query(`INSERT INTO crm_settings (mb_id, auto_enabled) VALUES ('${mbid}', ${newVal})`);
        }
        autoEnabled.value = !!newVal;
        console.log(`✅ 자동발송 ${label}`);
      } catch (e) {
        console.error('자동발송 설정 오류:', e);
        alert('설정 변경에 실패했습니다.');
      }
    }

    // 헤더 관련 함수들
    async function loadHeader() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT crm_header FROM user_settings WHERE mb_id = '${mbid}' LIMIT 1
      `);
      if (Array.isArray(rows) && rows.length > 0 && rows[0].crm_header) {
        const name = member_info?.mb_name || member_info?.name || '담당자';
        const phone = member_info?.mb_hp || member_info?.phone || '';
        msgHeader.value = rows[0].crm_header
          .replace(/\{\{my_name\}\}/g, name)
          .replace(/\{\{my_phone\}\}/g, phone);
        editingHeader.value = rows[0].crm_header;
      } else {
        msgHeader.value = defaultHeader.value;
        editingHeader.value = `☂️보 다™☂️\n👔 {{my_name}}이 보내셨습니다.\n`;
      }
    }

    function resetHeader() {
      editingHeader.value = `☂️보 다™☂️\n👔 {{my_name}}이 보내셨습니다.\n`;
    }

    function insertHeaderVar(varCode) {
      editingHeader.value = (editingHeader.value || '') + varCode;
    }

    async function saveHeader() {
      savingHeader.value = true;
      const mbid = getMbid();

      const escapeSQL = (str) => {
        if (!str) return '';
        return str
          .replace(/\\/g, '\\\\')
          .replace(/'/g, "''")
          .replace(/\n/g, '\\n')
          .replace(/\r/g, '\\r');
      };

      try {
        const existing = await sql_query(`SELECT idx FROM user_settings WHERE mb_id = '${mbid}' LIMIT 1`);
        
        if (Array.isArray(existing) && existing.length > 0) {
          await sql_query(`
            UPDATE user_settings SET crm_header = '${escapeSQL(editingHeader.value)}' WHERE mb_id = '${mbid}'
          `);
        } else {
          await sql_query(`
            INSERT INTO user_settings (mb_id, crm_header) VALUES ('${mbid}', '${escapeSQL(editingHeader.value)}')
          `);
        }

        const name = member_info?.mb_name || member_info?.name || '담당자';
        const phone = member_info?.mb_hp || member_info?.phone || '';
        msgHeader.value = editingHeader.value
          .replace(/\{\{my_name\}\}/g, name)
          .replace(/\{\{my_phone\}\}/g, phone);

        showHeaderEditor.value = false;
      } catch (e) {
        console.error('헤더 저장 오류:', e);
        alert('헤더 저장 중 오류가 발생했습니다.');
      } finally {
        savingHeader.value = false;
      }
    }

    function openMenu(event, itemIdx, type) {
      event.stopPropagation();
      const item = type === 'message' 
        ? messages.value.find(m => m.idx === itemIdx)
        : templates.value.find(t => t.idx === itemIdx);
      if (!item) return;

      const rect = event.currentTarget.getBoundingClientRect();
      
      menuState.visible = true;
      menuState.x = rect.right - 130;
      menuState.y = rect.bottom + 4;
      menuState.item = item;
      menuState.type = type;
    }

    function closeMenu() {
      menuState.visible = false;
      menuState.item = null;
      menuState.type = null;
    }

    async function handleMenuAction(action) {
      const item = menuState.item;
      closeMenu();
      if (!item) return;
      
      switch (action) {
        case 'edit':
          if (item.is_basic == 1) {
            if (!confirm('⚠️ 원본을 수정하면 이 메시지를 사용하는 모든 사용자에게 영향을 줍니다.\n계속하시겠습니까?')) return;
          }
          openMessageEditor(item);
          break;
        case 'toggle':
          await toggleMessageStatus(item);
          break;
        case 'toggleCommon':
          await toggleCommonMessage(item);
          break;
        case 'delete':
          await deleteMessage(item);
          break;
        case 'editTemplate':
          openTemplateEditor(item);
          break;
        case 'togglePublic':
          await toggleTemplatePublic(item);
          break;
        case 'deleteTemplate':
          await deleteTemplate(item);
          break;
      }
    }

    function openMessageViewer(msg) {
      viewingMessage.value = msg;
      showMessageViewer.value = true;

      // 공통 메시지면 유저 설정 로드
      if (msg.is_basic == 1) {
        const setting = getUserMsgSetting(msg);
        if (setting) {
          let filter = [];
          try { filter = JSON.parse(setting.target_filter || '[]'); if (!Array.isArray(filter)) filter = []; } catch { filter = []; }
          Object.assign(viewerUserSetting, {
            msg_idx: msg.idx,
            settingIdx: setting.idx,
            enabled: setting.enabled == 1,
            target_filter: filter,
            target_count: setting.target_count || 0,
            attach_gift: setting.attach_gift == 1,
            gift_product: setting.gift_product || 'random',
            gift_max_count: setting.gift_max_count || 10,
            saving: false,
          });
          selectedCustomers.value = [...filter];
        } else {
          Object.assign(viewerUserSetting, {
            msg_idx: msg.idx,
            settingIdx: null,
            enabled: true,
            target_filter: [],
            target_count: 0,
            attach_gift: false,
            gift_product: 'random',
            gift_max_count: 10,
            saving: false,
          });
          selectedCustomers.value = [];
        }
      }
    }

    function openTemplateViewer(tpl) {
      viewingTemplate.value = tpl;
      showTemplateViewer.value = true;
    }

    function openTemplateEditor(tpl) {
      if (tpl) {
        Object.assign(editingTemplate, {
          idx: tpl.idx,
          title: tpl.title,
          content: tpl.content,
          image: tpl.image || null,
          category: tpl.category || 'general',
          is_public: tpl.is_public == 1
        });
      } else {
        Object.assign(editingTemplate, {
          idx: null,
          title: '',
          content: '',
          image: null,
          category: 'general',
          is_public: false
        });
      }
      showTemplateEditor.value = true;
    }

    async function saveTemplate() {
      if (!editingTemplate.title || !editingTemplate.content) {
        alert('제목과 내용을 입력해주세요.');
        return;
      }

      savingTemplate.value = true;
      const mbid = getMbid();

      const escapeSQL = (str) => {
        if (!str) return '';
        return str
          .replace(/\\/g, '\\\\')
          .replace(/'/g, "''")
          .replace(/\n/g, '\\n')
          .replace(/\r/g, '\\r');
      };

      const titleEsc = escapeSQL(editingTemplate.title);
      const contentEsc = escapeSQL(editingTemplate.content);

      try {
        if (editingTemplate.idx) {
          const whereClause = isAdmin.value 
            ? `WHERE idx = ${editingTemplate.idx}`
            : `WHERE idx = ${editingTemplate.idx} AND mb_id = '${mbid}'`;
          
          await sql_query(`
            UPDATE crm_templates SET
              title = '${titleEsc}',
              content = '${contentEsc}',
              image = ${editingTemplate.image ? `'${editingTemplate.image}'` : 'NULL'},
              category = '${editingTemplate.category}',
              is_public = ${editingTemplate.is_public ? 1 : 0}
            ${whereClause}
          `);
        } else {
          await sql_query(`
            INSERT INTO crm_templates 
            (mb_id, title, content, image, category, is_public, status, use_count)
            VALUES (
              '${mbid}', 
              '${titleEsc}', 
              '${contentEsc}',
              ${editingTemplate.image ? `'${editingTemplate.image}'` : 'NULL'},
              '${editingTemplate.category}',
              ${editingTemplate.is_public ? 1 : 0},
              1,
              0
            )
          `);
        }

        showTemplateEditor.value = false;
        loadTemplates();
      } catch (e) {
        console.error('템플릿 저장 오류:', e);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        savingTemplate.value = false;
      }
    }

    async function toggleTemplatePublic(tpl) {
      const newPublic = tpl.is_public == 1 ? 0 : 1;
      const mbid = getMbid();
      const whereClause = isAdmin.value 
        ? `WHERE idx=${tpl.idx}`
        : `WHERE idx=${tpl.idx} AND mb_id='${mbid}'`;
      await sql_query(`UPDATE crm_templates SET is_public=${newPublic} ${whereClause}`);
      loadTemplates();
    }

    async function deleteTemplate(tpl) {
      if (!confirm('정말 삭제하시겠습니까?')) return;
      const mbid = getMbid();
      const whereClause = isAdmin.value 
        ? `WHERE idx=${tpl.idx}`
        : `WHERE idx=${tpl.idx} AND mb_id='${mbid}'`;
      await sql_query(`DELETE FROM crm_templates ${whereClause}`);
      loadTemplates();
    }

    function triggerTemplateImageUpload() {
      templateImageInput.value?.click();
    }

    async function handleTemplateImageUpload(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      editingTemplate.image = URL.createObjectURL(file);

      const formData = new FormData();
      formData.append('idx', editingTemplate.idx || '');
      formData.append('table', 'crm_templates');
      formData.append('usage', 'crm_template_image');
      formData.append('user', getMbid());
      formData.append('file', file);

      try {
        const response = await fetch('/api/upload/image', {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        
        if (data.url) {
          editingTemplate.image = data.url;
        } else {
          alert('이미지 업로드 실패');
          editingTemplate.image = null;
        }
      } catch (err) {
        console.error('업로드 오류:', err);
        alert('업로드 중 오류 발생');
        editingTemplate.image = null;
      }
    }

    function insertTemplateVariable(code) {
      const textarea = templateTextarea.value;
      if (!textarea) {
        editingTemplate.content = (editingTemplate.content || '') + code;
        return;
      }
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editingTemplate.content || '';
      
      editingTemplate.content = text.slice(0, start) + code + text.slice(end);
      
      nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(start + code.length, start + code.length);
      });
    }

    function useTemplate(tpl) {
      showTemplateViewer.value = false;
      
      Object.assign(editingMessage, {
        idx: null,
        title: tpl.title,
        content: tpl.content,
        image: tpl.image || null,
        type: 'auto',
        trigger_type: tpl.category === 'birthday' ? 'birthday' : 
                      tpl.category === 'payment' ? 'payment_due' : 'custom',
        trigger_days: 0,
        trigger_date: '',
        trigger_time: '09:00',
        schedule_date: '',
        status: 'active',
        is_basic: false,
        use_header: true
      });
      excludeList.value = [];
      selectedCustomers.value = [];
      activeTab.value = 'personal';
      showEditor.value = true;

      if (tpl.idx) {
        sql_query(`UPDATE crm_templates SET use_count = use_count + 1 WHERE idx = ${tpl.idx}`);
      }
    }

    function openMessageEditor(msg, forceBasic = false) {
      if (msg) {
        let selected = [];
        try {
          selected = JSON.parse(msg.target_filter || '[]');
          if (!Array.isArray(selected)) selected = [];
        } catch { selected = []; }
        
        Object.assign(editingMessage, {
          idx: msg.idx,
          title: msg.title,
          content: msg.content,
          image: msg.image || null,
          type: msg.type || 'auto',
          trigger_type: msg.trigger_type || 'birthday',
          trigger_days: msg.trigger_days || 0,
          trigger_date: msg.trigger_date || '',
          trigger_time: msg.trigger_time?.slice(0, 5) || '09:00',
          schedule_date: msg.schedule_date ? msg.schedule_date.replace(' ', 'T').slice(0, 16) : '',
          status: msg.status || 'active',
          is_basic: msg.is_basic == 1,
          use_header: msg.use_header == null ? true : msg.use_header == 1,
          attach_gift: msg.attach_gift == 1,
          gift_product: msg.gift_product || 'random',
          gift_max_count: msg.gift_max_count || 10,
        });
        selectedCustomers.value = selected;
      } else {
        Object.assign(editingMessage, {
          idx: null,
          title: '',
          content: '',
          image: null,
          type: 'auto',
          trigger_type: 'birthday',
          trigger_days: 0,
          trigger_date: '',
          trigger_time: '09:00',
          schedule_date: '',
          status: 'active',
          is_basic: forceBasic,
          use_header: true,
          attach_gift: false,
          gift_product: 'random',
          gift_max_count: 10,
        });
        selectedCustomers.value = [];
      }
      showEditor.value = true;
    }

    function triggerImageUpload() {
      imageInput.value?.click();
    }

    async function handleImageUpload(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      editingMessage.image = URL.createObjectURL(file);

      const formData = new FormData();
      formData.append('idx', editingMessage.idx || '');
      formData.append('table', 'crm_messages');
      formData.append('usage', 'crm_image');
      formData.append('user', getMbid());
      formData.append('file', file);

      try {
        const response = await fetch('/api/upload/image', {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        
        if (data.url) {
          editingMessage.image = data.url;
        } else {
          alert('이미지 업로드 실패');
          editingMessage.image = null;
        }
      } catch (err) {
        console.error('업로드 오류:', err);
        alert('업로드 중 오류 발생');
        editingMessage.image = null;
      }
    }

    function insertVariable(code) {
      const textarea = messageTextarea.value;
      if (!textarea) {
        editingMessage.content = (editingMessage.content || '') + code;
        return;
      }
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editingMessage.content || '';
      
      editingMessage.content = text.slice(0, start) + code + text.slice(end);
      
      nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(start + code.length, start + code.length);
      });
    }

    // 유저별 공통메시지 설정 저장
    async function saveUserMsgSetting() {
      const mbid = getMbid();
      const msgIdx = viewerUserSetting.msg_idx;
      if (!msgIdx) return;

      viewerUserSetting.saving = true;
      const filterJson = JSON.stringify(selectedCustomers.value);
      const count = selectedCustomers.value.length;

      try {
        if (viewerUserSetting.settingIdx) {
          await sql_query(`
            UPDATE crm_message_user_settings SET
              enabled = ${viewerUserSetting.enabled ? 1 : 0},
              target_filter = '${filterJson}',
              target_count = ${count},
              attach_gift = ${viewerUserSetting.attach_gift ? 1 : 0},
              gift_product = ${viewerUserSetting.attach_gift ? `'${viewerUserSetting.gift_product || 'random'}'` : 'NULL'},
              gift_max_count = ${viewerUserSetting.gift_max_count || 10}
            WHERE idx = ${viewerUserSetting.settingIdx}
          `);
        } else {
          const result = await sql_query(`
            INSERT INTO crm_message_user_settings 
            (msg_idx, mb_id, enabled, target_filter, target_count, attach_gift, gift_product, gift_max_count)
            VALUES (
              ${msgIdx}, '${mbid}', ${viewerUserSetting.enabled ? 1 : 0},
              '${filterJson}', ${count},
              ${viewerUserSetting.attach_gift ? 1 : 0},
              ${viewerUserSetting.attach_gift ? `'${viewerUserSetting.gift_product || 'random'}'` : 'NULL'},
              ${viewerUserSetting.gift_max_count || 10}
            )
          `);
          viewerUserSetting.settingIdx = result?.insertId;
        }
        await loadUserMsgSettings();
        alert('설정이 저장되었습니다.');
      } catch (e) {
        console.error('설정 저장 오류:', e);
        alert('설정 저장 실패');
      } finally {
        viewerUserSetting.saving = false;
      }
    }

    // 원본 수정 (관리자)
    function editOriginalMessage() {
      if (!confirm('⚠️ 원본을 수정하면 이 메시지를 사용하는 모든 사용자에게 영향을 줍니다.\n계속하시겠습니까?')) return;
      const msg = viewingMessage.value;
      showMessageViewer.value = false;
      openMessageEditor(msg);
    }

    async function copyMessageToMine(msg) {
      if (!confirm(`"${msg.title}"을(를) 내 메시지로 복사할까요?`)) return;
      const mbid = getMbid();
      const escapeSQL = (str) => {
        if (!str) return '';
        return str.replace(/\\/g, '\\\\').replace(/'/g, "''").replace(/\n/g, '\\n').replace(/\r/g, '\\r');
      };
      try {
        await sql_query(`
          INSERT INTO crm_messages 
          (mb_id, title, content, image, type, trigger_type, trigger_days, trigger_date, trigger_time, status, is_basic, use_header, attach_gift, gift_product, gift_max_count, target_count, target_tags, target_filter)
          VALUES (
            '${mbid}', '${escapeSQL(msg.title)}', '${escapeSQL(msg.content)}',
            ${msg.image ? `'${msg.image}'` : 'NULL'},
            '${msg.type}', '${msg.trigger_type}', ${msg.trigger_days || 0},
            ${msg.trigger_date ? `'${msg.trigger_date}'` : 'NULL'},
            '${msg.trigger_time || '09:00'}', 'active', 0,
            ${msg.use_header == 1 ? 1 : 0}, 0, NULL, 10, 0, '[]', '[]'
          )
        `);
        alert('내 메시지로 복사되었습니다.');
        loadMessages();
      } catch (e) {
        alert('복사 실패: ' + e.message);
      }
    }

    async function copyPackToMine(pack) {
      if (!confirm(`"${pack.pack_name}"을(를) 내 팩으로 복사할까요?`)) return;
      const mbid = getMbid();
      const escapeSQL = (str) => {
        if (!str) return '';
        return str.replace(/\\/g, '\\\\').replace(/'/g, "''").replace(/\n/g, '\\n').replace(/\r/g, '\\r');
      };
      try {
        const result = await sql_query(`
          INSERT INTO crm_message_packs 
          (mb_id, pack_name, trigger_type, trigger_days, trigger_date, trigger_time, use_header, status, is_basic, attach_gift, gift_product, gift_max_count, target_count, target_tags, target_filter)
          VALUES (
            '${mbid}', '${escapeSQL(pack.pack_name)}',
            '${pack.trigger_type}', ${pack.trigger_days || 0},
            ${pack.trigger_date ? `'${pack.trigger_date}'` : 'NULL'},
            '${pack.trigger_time || '09:00'}', ${pack.use_header == 1 ? 1 : 0},
            'active', 0, 0, NULL, 10, 0, '[]', '[]'
          )
        `);
        const newIdx = result?.insertId || result?.[0]?.insertId;
        let packIdx = newIdx;
        if (!packIdx) {
          const lastRow = await sql_query(`SELECT MAX(idx) as id FROM crm_message_packs WHERE mb_id = '${mbid}'`);
          packIdx = lastRow?.[0]?.id;
        }
        if (packIdx) {
          const items = await sql_query(`SELECT * FROM crm_pack_items WHERE pack_idx = ${pack.idx} ORDER BY sort_order`);
          if (Array.isArray(items)) {
            for (const item of items) {
              await sql_query(`
                INSERT INTO crm_pack_items (pack_idx, title, content, image, sort_order)
                VALUES (${packIdx}, '${escapeSQL(item.title)}', '${escapeSQL(item.content)}', ${item.image ? `'${item.image}'` : 'NULL'}, ${item.sort_order || 0})
              `);
            }
          }
        }
        alert('내 팩으로 복사되었습니다.');
        loadPacks();
      } catch (e) {
        alert('복사 실패: ' + e.message);
      }
    }

    // 직접 발송 관련 함수들
    function fabAction(type) {
      fabOpen.value = false;
      if (type === 'auto') {
        openMessageEditor(null);
      } else if (type === 'common') {
        openMessageEditor(null, true);
      } else if (type === 'instant') {
        openInstantSender(false);
      } else if (type === 'group') {
        openInstantSender(true);
      } else if (type === 'pack') {
        openPackEditor();
      }
    }

    function openInstantSender(groupMode = false) {
      fabOpen.value = false;
      Object.assign(instantMessage, {
        title: '',
        content: '',
        image: null,
        use_header: true
      });
      instantRecipients.value = [];
      recipientSearch.value = '';
      selectedGroupTags.value = [];
      selectedVipGrades.value = [];
      selectedManageRanks.value = [];
      selectedMTypes.value = [];
      isGroupMode.value = groupMode;
      instantGiftEnabled.value = false;
      instantGiftProduct.value = 'random';
      instantGiftMax.value = 10;
      giftAssignMode.value = 'bulk';
      giftAssignments.value = {};
      showBulkApply.value = false;
      bulkApplyProduct.value = '';
      showInstantSender.value = true;
    }

    // 그룹 태그 토글 (직접 발송)
    function toggleGroupTag(tagName) {
      const idx = selectedGroupTags.value.indexOf(tagName);
      if (idx > -1) {
        selectedGroupTags.value.splice(idx, 1);
      } else {
        selectedGroupTags.value.push(tagName);
      }
      autoSelectByFilters();
    }

    // 필터 기반 자동 선택 (태그 + 등급 + 관계)
    function autoSelectByFilters() {
      const hasTags = selectedGroupTags.value.length > 0;
      const hasVip = selectedVipGrades.value.length > 0;
      const hasManage = selectedManageRanks.value.length > 0;
      const hasMType = selectedMTypes.value.length > 0;

      if (!hasTags && !hasVip && !hasManage && !hasMType) {
        instantRecipients.value = [];
        return;
      }
      const matching = customers.value.filter(c => {
        // 각 필터는 OR 관계 (하나라도 매칭되면 포함)
        // 하지만 카테고리 간은 AND 관계 (태그 + 등급 모두 매칭)
        let pass = true;
        if (hasTags) {
          let tags = [];
          try {
            tags = typeof c.tags === 'string' ? JSON.parse(c.tags) : (c.tags || []);
          } catch { tags = []; }
          if (!selectedGroupTags.value.some(t => tags.includes(t))) pass = false;
        }
        if (hasVip && !selectedVipGrades.value.includes(Number(c.vip_rank))) pass = false;
        if (hasManage && !selectedManageRanks.value.includes(String(c.manage_rank))) pass = false;
        if (hasMType && !selectedMTypes.value.includes(c.m_type)) pass = false;
        return pass;
      });
      instantRecipients.value = matching.map(c => c.idx);
    }

    // 고객 태그 파싱 헬퍼
    function getCustomerTags(customer) {
      try {
        const tags = typeof customer.tags === 'string' ? JSON.parse(customer.tags) : (customer.tags || []);
        return Array.isArray(tags) ? tags : [];
      } catch { return []; }
    }

    function closeInstantSender() {
      if (instantMessage.content || instantRecipients.value.length > 0) {
        if (!confirm('작성 중인 내용이 있습니다. 취소하시겠습니까?')) return;
      }
      showInstantSender.value = false;
    }

    function toggleRecipient(customerIdx) {
      const idx = instantRecipients.value.indexOf(customerIdx);
      if (idx > -1) {
        instantRecipients.value.splice(idx, 1);
      } else {
        instantRecipients.value.push(customerIdx);
      }
    }

    function toggleAllRecipients() {
      if (isAllRecipientsSelected.value) {
        const filteredIdxs = filteredRecipientsCustomers.value.map(c => c.idx);
        instantRecipients.value = instantRecipients.value.filter(
          idx => !filteredIdxs.includes(idx)
        );
      } else {
        const filteredIdxs = filteredRecipientsCustomers.value.map(c => c.idx);
        instantRecipients.value = [...new Set([...instantRecipients.value, ...filteredIdxs])];
      }
    }

    function triggerInstantImageUpload() {
      instantImageInput.value?.click();
    }

    async function handleInstantImageUpload(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      instantMessage.image = URL.createObjectURL(file);

      const formData = new FormData();
      formData.append('table', 'crm_instant');
      formData.append('usage', 'crm_instant_image');
      formData.append('user', getMbid());
      formData.append('file', file);

      try {
        const response = await fetch('/api/upload/image', {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        
        if (data.url) {
          instantMessage.image = data.url;
        } else {
          alert('이미지 업로드 실패');
          instantMessage.image = null;
        }
      } catch (err) {
        console.error('업로드 오류:', err);
        alert('업로드 중 오류 발생');
        instantMessage.image = null;
      }
    }

    function insertInstantVariable(code) {
      const textarea = instantTextarea.value;
      if (!textarea) {
        instantMessage.content = (instantMessage.content || '') + code;
        return;
      }
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = instantMessage.content || '';
      
      instantMessage.content = text.slice(0, start) + code + text.slice(end);
      
      nextTick(() => {
        textarea.focus();
        textarea.setSelectionRange(start + code.length, start + code.length);
      });
    }

    async function sendInstantMessage() {
      if (!instantMessage.content) {
        alert('메시지 내용을 입력해주세요.');
        return;
      }
      if (instantRecipients.value.length === 0) {
        alert('수신자를 선택해주세요.');
        return;
      }

      let giftInfo = '';
      if (instantGiftEnabled.value) {
        if (giftAssignMode.value === 'individual') {
          giftInfo = `\n\n🎁 개별 선물 지정: ${giftAssignedCount.value}명`;
        } else {
          giftInfo = `\n\n🎁 선물 첨부: ${instantGiftProduct.value === 'random' ? '랜덤' : instantGiftProduct.value} (최대 ${instantGiftMax.value}개)`;
        }
      }
      
      if (!confirm(`${instantRecipients.value.length}명에게 메시지를 발송하시겠습니까?${giftInfo}`)) {
        return;
      }

      sendingInstant.value = true;
      const mbid = getMbid();

      try {
        const recipientIdxs = instantRecipients.value.join(',');
        const recipientRows = await sql_query(`
          SELECT idx, name, nickname, title, phone, birth 
          FROM customers 
          WHERE idx IN (${recipientIdxs})
        `);

        if (!Array.isArray(recipientRows) || recipientRows.length === 0) {
          throw new Error('수신자 정보를 찾을 수 없습니다.');
        }

        const replaceVariables = (content, customer) => {
          return content
            .replace(/\{\{name\}\}/g, customer.name || '')
            .replace(/\{\{nickname\}\}/g, customer.nickname || customer.name || '')
            .replace(/\{\{title\}\}/g, customer.title || '고객님')
            .replace(/\{\{phone\}\}/g, customer.phone || '')
            .replace(/\{\{birth\}\}/g, customer.birth || '');
        };

        let giftSentCount = 0;
        const isIndividualMode = giftAssignMode.value === 'individual';
        const giftMax = (!isIndividualMode && instantGiftEnabled.value) ? instantGiftMax.value : Infinity;
        const giftProduct = instantGiftProduct.value;

        const sendPromises = recipientRows.map(async (customer) => {
          let personalizedContent = replaceVariables(instantMessage.content, customer);
          
          if (instantMessage.use_header) {
            personalizedContent = msgHeader.value + personalizedContent;
          }

          // 🎁 선물 링크 생성
          if (instantGiftEnabled.value) {
            let shouldSendGift = false;
            let productFilter = giftProduct;

            if (isIndividualMode) {
              // 개별 지정 모드: 해당 고객에게 지정된 선물이 있으면 발송
              const assignedProduct = giftAssignments.value[customer.idx];
              if (assignedProduct && assignedProduct !== '') {
                shouldSendGift = true;
                productFilter = assignedProduct;
              }
            } else {
              // 일괄 배정 모드: 최대 수량까지 발송
              shouldSendGift = giftSentCount < giftMax;
            }

            if (shouldSendGift) {
              try {
                const giftUrl = await createGiftLink(customer, productFilter);
                if (giftUrl) {
                  personalizedContent += `\n\n🎁 선물이 도착했어요!\n👉 ${giftUrl}`;
                  giftSentCount++;
                }
              } catch (e) {
                console.error(`선물 링크 생성 실패 (${customer.name}):`, e);
              }
            }
          }
          
          return fetch('/api/crm/instant-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              mb_id: mbid,
              customer_idx: customer.idx,
              customer_name: customer.name,
              customer_phone: customer.phone,
              content: personalizedContent,
              image: instantMessage.image || null
            })
          })
          .then(r => r.json())
          .then(result => ({ success: result.success, name: customer.name }))
          .catch(err => {
            console.error(`발송 실패 (${customer.name}):`, err);
            return { success: false, name: customer.name };
          });
        });

        const results = await Promise.all(sendPromises);
        const successCount = results.filter(r => r.success).length;
        const failCount = results.length - successCount;

        let resultMsg = `발송 완료!\n성공: ${successCount}건`;
        if (failCount > 0) resultMsg += `\n실패: ${failCount}건`;
        if (instantGiftEnabled.value) resultMsg += `\n🎁 선물 발송: ${giftSentCount}건`;
        alert(resultMsg);

        showInstantSender.value = false;
        loadHistory();
        if (instantGiftEnabled.value) loadGiftProducts(); // 재고 갱신

      } catch (e) {
        console.error('발송 오류:', e);
        alert('발송 중 오류가 발생했습니다: ' + e.message);
      } finally {
        sendingInstant.value = false;
      }
    }

    // API Calls
    async function checkAdminStatus() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT level FROM member WHERE mb_id = '${mbid}' LIMIT 1
      `);
      if (Array.isArray(rows) && rows.length > 0) {
        isAdmin.value = rows[0].level == 1;
      }
    }

    async function loadMessages() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT * FROM crm_messages 
        WHERE mb_id = '${mbid}' OR is_basic = 1
        ORDER BY created_at DESC
      `);
      if (Array.isArray(rows)) messages.value = rows;
      // 공통 메시지 유저 설정도 함께 로드
      await loadUserMsgSettings();
    }

    async function loadUserMsgSettings() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT * FROM crm_message_user_settings WHERE mb_id = '${mbid}'
      `);
      if (Array.isArray(rows)) {
        const map = {};
        rows.forEach(r => { map[r.msg_idx] = r; });
        userMsgSettings.value = map;
      }
    }

    function getUserMsgSetting(msg) {
      if (!msg || msg.is_basic != 1) return null;
      return userMsgSettings.value[msg.idx] || null;
    }

    // 팩 유저 설정
    async function loadUserPackSettings() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT * FROM crm_pack_user_settings WHERE mb_id = '${mbid}'
      `);
      if (Array.isArray(rows)) {
        const map = {};
        rows.forEach(r => { map[r.pack_idx] = r; });
        userPackSettings.value = map;
      }
    }

    function getUserPackSetting(pack) {
      if (!pack || pack.is_basic != 1) return null;
      return userPackSettings.value[pack.idx] || null;
    }

    async function openPackViewer(pack) {
      viewingPack.value = pack;
      showPackViewer.value = true;

      // 팩 아이템 로드
      const items = await sql_query(`
        SELECT * FROM crm_pack_items WHERE pack_idx = ${pack.idx} ORDER BY sort_order, idx
      `);
      viewingPackItems.value = Array.isArray(items) ? items : [];

      // 유저 설정 로드
      const setting = getUserPackSetting(pack);
      if (setting) {
        let filter = [];
        try { filter = JSON.parse(setting.target_filter || '[]'); if (!Array.isArray(filter)) filter = []; } catch { filter = []; }
        let selItems = [];
        try { selItems = JSON.parse(setting.selected_items || '[]'); if (!Array.isArray(selItems)) selItems = []; } catch { selItems = []; }
        // selected_items가 비어있으면 전체 선택으로 간주
        if (selItems.length === 0) selItems = viewingPackItems.value.map(it => it.idx);
        Object.assign(viewerPackSetting, {
          pack_idx: pack.idx,
          settingIdx: setting.idx,
          enabled: setting.enabled == 1,
          target_filter: filter,
          target_count: setting.target_count || 0,
          attach_gift: setting.attach_gift == 1,
          gift_product: setting.gift_product || 'random',
          gift_max_count: setting.gift_max_count || 10,
          selected_items: selItems,
          saving: false,
        });
        selectedCustomers.value = [...filter];
      } else {
        // 설정 없으면 전체 선택
        Object.assign(viewerPackSetting, {
          pack_idx: pack.idx,
          settingIdx: null,
          enabled: true,
          target_filter: [],
          target_count: 0,
          attach_gift: false,
          gift_product: 'random',
          gift_max_count: 10,
          selected_items: viewingPackItems.value.map(it => it.idx),
          saving: false,
        });
        selectedCustomers.value = [];
      }
    }

    function isPackItemSelected(item) {
      return viewerPackSetting.selected_items.includes(item.idx);
    }

    function togglePackItem(item) {
      const idx = item.idx;
      const arr = viewerPackSetting.selected_items;
      const i = arr.indexOf(idx);
      if (i > -1) {
        // 최소 1개는 선택되어야 함
        if (arr.length <= 1) return;
        arr.splice(i, 1);
      } else {
        arr.push(idx);
      }
    }

    const selectedPackItemCount = computed(() => {
      return viewerPackSetting.selected_items.length;
    });

    async function saveUserPackSetting() {
      const mbid = getMbid();
      const packIdx = viewerPackSetting.pack_idx;
      if (!packIdx) return;

      viewerPackSetting.saving = true;
      const filterJson = JSON.stringify(selectedCustomers.value);
      const count = selectedCustomers.value.length;
      const selectedItemsJson = JSON.stringify(viewerPackSetting.selected_items);

      try {
        if (viewerPackSetting.settingIdx) {
          await sql_query(`
            UPDATE crm_pack_user_settings SET
              enabled = ${viewerPackSetting.enabled ? 1 : 0},
              target_filter = '${filterJson}',
              target_count = ${count},
              attach_gift = ${viewerPackSetting.attach_gift ? 1 : 0},
              gift_product = ${viewerPackSetting.attach_gift ? `'${viewerPackSetting.gift_product || 'random'}'` : 'NULL'},
              gift_max_count = ${viewerPackSetting.gift_max_count || 10},
              selected_items = '${selectedItemsJson}'
            WHERE idx = ${viewerPackSetting.settingIdx}
          `);
        } else {
          const result = await sql_query(`
            INSERT INTO crm_pack_user_settings 
            (pack_idx, mb_id, enabled, target_filter, target_count, attach_gift, gift_product, gift_max_count, selected_items)
            VALUES (
              ${packIdx}, '${mbid}', ${viewerPackSetting.enabled ? 1 : 0},
              '${filterJson}', ${count},
              ${viewerPackSetting.attach_gift ? 1 : 0},
              ${viewerPackSetting.attach_gift ? `'${viewerPackSetting.gift_product || 'random'}'` : 'NULL'},
              ${viewerPackSetting.gift_max_count || 10},
              '${selectedItemsJson}'
            )
          `);
          viewerPackSetting.settingIdx = result?.insertId;
        }
        await loadUserPackSettings();
        alert('설정이 저장되었습니다.');
      } catch (e) {
        console.error('팩 설정 저장 오류:', e);
        alert('설정 저장 실패');
      } finally {
        viewerPackSetting.saving = false;
      }
    }

    function editOriginalPack() {
      if (!confirm('⚠️ 원본을 수정하면 이 팩을 사용하는 모든 사용자에게 영향을 줍니다.\n계속하시겠습니까?')) return;
      const pack = viewingPack.value;
      showPackViewer.value = false;
      openPackEditor(pack);
    }

    async function loadTemplates() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT * FROM crm_templates 
        WHERE (mb_id='${mbid}' OR is_public=1) AND status=1 
        ORDER BY use_count DESC
      `);
      if (Array.isArray(rows)) templates.value = rows;
    }

    async function loadHistory() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT * FROM crm_send_logs 
        WHERE mb_id='${mbid}' 
        ORDER BY sent_at DESC 
        LIMIT 200
      `);
      if (Array.isArray(rows)) historyLogs.value = rows;
    }

    async function loadCustomers() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT idx, name, phone, tags, vip_rank, manage_rank, m_type FROM customers 
        WHERE mb_id = '${mbid}' 
        ORDER BY name ASC
      `);
      if (Array.isArray(rows)) customers.value = rows;
    }

    async function loadCustomerTags() {
      const mbid = getMbid();
      const rows = await sql_query(`
        SELECT * FROM customer_tag WHERE mb_id = '${mbid}' ORDER BY name ASC
      `);
      if (Array.isArray(rows)) customerTags.value = rows;
    }

    async function saveMessage(isDraft = false) {
      if (!editingMessage.title || !editingMessage.content) {
        alert('제목과 내용을 입력해주세요.');
        return;
      }

      if (!isDraft && editingMessage.type === 'scheduled' && !editingMessage.schedule_date) {
        alert('예약 일시를 선택해주세요.');
        return;
      }

      if (!isAdmin.value && editingMessage.is_basic) {
        editingMessage.is_basic = false;
      }

      saving.value = true;
      const mbid = getMbid();
      const targetCount = selectedCustomers.value.length;
      const selectedJson = JSON.stringify(selectedCustomers.value);
      const status = isDraft ? 'draft' : 'active';

      const escapeSQL = (str) => {
        if (!str) return '';
        return str
          .replace(/\\/g, '\\\\')
          .replace(/'/g, "''")
          .replace(/\n/g, '\\n')
          .replace(/\r/g, '\\r');
      };

      const titleEsc = escapeSQL(editingMessage.title);
      const contentEsc = escapeSQL(editingMessage.content);

      const formatDateFn = (dateStr) => {
        if (!dateStr) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        return dateStr.slice(0, 10);
      };

      const triggerDateFormatted = formatDateFn(editingMessage.trigger_date);
      const scheduleDateFormatted = editingMessage.schedule_date 
        ? editingMessage.schedule_date.replace('T', ' ').slice(0, 16) 
        : null;

      try {
        if (editingMessage.idx) {
          await sql_query(`
            UPDATE crm_messages SET
              title = '${titleEsc}',
              content = '${contentEsc}',
              image = ${editingMessage.image ? `'${editingMessage.image}'` : 'NULL'},
              type = '${editingMessage.type}',
              trigger_type = '${editingMessage.trigger_type}',
              trigger_days = ${editingMessage.trigger_days || 0},
              trigger_date = ${triggerDateFormatted ? `'${triggerDateFormatted}'` : 'NULL'},
              trigger_time = '${editingMessage.trigger_time || '09:00'}',
              schedule_date = ${scheduleDateFormatted ? `'${scheduleDateFormatted}'` : 'NULL'},
              target_count = ${targetCount},
              target_filter = '${selectedJson}',
              status = '${status}',
              is_basic = ${editingMessage.is_basic ? 1 : 0},
              use_header = ${editingMessage.use_header ? 1 : 0},
              attach_gift = ${editingMessage.attach_gift ? 1 : 0},
              gift_product = ${editingMessage.attach_gift ? `'${editingMessage.gift_product || 'random'}'` : 'NULL'},
              gift_max_count = ${editingMessage.gift_max_count || 10}
            WHERE idx = ${editingMessage.idx}
          `);
        } else {
          await sql_query(`
            INSERT INTO crm_messages 
            (mb_id, title, content, image, type, trigger_type, trigger_days, trigger_date, trigger_time, schedule_date, target_count, target_filter, status, is_basic, use_header, attach_gift, gift_product, gift_max_count)
            VALUES (
              '${mbid}', '${titleEsc}', '${contentEsc}',
              ${editingMessage.image ? `'${editingMessage.image}'` : 'NULL'},
              '${editingMessage.type}', '${editingMessage.trigger_type}',
              ${editingMessage.trigger_days || 0},
              ${triggerDateFormatted ? `'${triggerDateFormatted}'` : 'NULL'},
              '${editingMessage.trigger_time || '09:00'}',
              ${scheduleDateFormatted ? `'${scheduleDateFormatted}'` : 'NULL'},
              ${targetCount}, '${selectedJson}', '${status}',
              ${editingMessage.is_basic ? 1 : 0},
              ${editingMessage.use_header ? 1 : 0},
              ${editingMessage.attach_gift ? 1 : 0},
              ${editingMessage.attach_gift ? `'${editingMessage.gift_product || 'random'}'` : 'NULL'},
              ${editingMessage.gift_max_count || 10}
            )
          `);
        }

        showEditor.value = false;
        loadMessages();
        loadPacks();
      } catch (e) {
        console.error('저장 오류:', e);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        saving.value = false;
      }
    }

    async function toggleMessageStatus(msg) {
      const newStatus = msg.status === 'active' ? 'paused' : 'active';
      await sql_query(`UPDATE crm_messages SET status='${newStatus}' WHERE idx=${msg.idx}`);
      loadMessages();
    }

    async function toggleCommonMessage(msg) {
      if (!isAdmin.value) return;
      const newBasic = msg.is_basic == 1 ? 0 : 1;
      await sql_query(`UPDATE crm_messages SET is_basic=${newBasic} WHERE idx=${msg.idx}`);
      loadMessages();
    }

    async function deleteMessage(msg) {
      if (!confirm('정말 삭제하시겠습니까?')) return;
      await sql_query(`DELETE FROM crm_messages WHERE idx=${msg.idx}`);
      loadMessages();
    }

    function handleKeydown(e) {
      if (e.key === 'Escape' && menuState.visible) {
        closeMenu();
      }
    }

    // Init
    onMounted(() => {
      checkAdminStatus();
      loadMessages();
      loadTemplates();
      loadHistory();
      loadCustomers();
      loadCustomerTags();
      loadHeader();
      loadAutoEnabled();
      loadPacks();
      loadGiftProducts();
      document.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeydown);
    });

    return {
      activeTab, searchQuery, searchFocused, fabOpen, sortOption, tabs,
      showEditor, showCustomerPicker, showTemplateViewer, showTemplateEditor,
      showMessageViewer, viewingMessage, showHeaderEditor,
      showInstantSender, sendingInstant, recipientSearch, instantRecipients,
      showBmsTest,
      instantImageInput, instantTextarea, instantMessage,
      isGroupMode, customerTags, selectedGroupTags,
      instantGiftEnabled, instantGiftProduct, instantGiftMax, giftProducts, giftStockTotal, loadGiftProducts,
      // 개별 선물 지정
      giftAssignMode, giftAssignments, giftAssignedCount, showBulkApply, bulkApplyProduct,
      setGiftAssignment, applyGiftToAll, confirmBulkApply,
      getCustomerName, getCustomerVipRank, getCustomerVipLabel,
      getCustomerManageRankLabel, getCustomerMType,
      // 고객 등급 필터
      vipGrades, manageRankOptions, mTypeOptions, activeManageRanks, activeMTypes,
      selectedVipGrades, selectedManageRanks, selectedMTypes,
      pickerVipFilter, pickerManageFilter, pickerMTypeFilter,
      getVipLabel, getManageRankLabel, getManageRankShort, getMTypeLabel,
      getVipGradeCount, getManageRankCount, getMTypeCount,
      toggleVipGrade, toggleManageRank, toggleMType, hasAnyGroupFilter,
      filteredRecipientsCustomers, isAllRecipientsSelected,
      instantContentLength, instantTotalLength, instantMsgType, instantMsgTypeLabel,
      openInstantSender, closeInstantSender, fabAction, toggleRecipient, toggleAllRecipients,
      toggleGroupTag, getCustomerTags,
      triggerInstantImageUpload, handleInstantImageUpload, insertInstantVariable, sendInstantMessage,
      msgHeader, editingHeader, defaultHeader, headerLength,
      autoEnabled, toggleAutoSend,
      existingPacks,
      showPackEditor, savingPack, packSettings, packItems, packImageInput,
      openPackEditor, closePackEditor, addPackItem, removePackItem,
      triggerPackImageUpload, handlePackImageUpload, savePack,
      loadPackItems, deletePack, getPackScheduleText,
      loadHeader, resetHeader, insertHeaderVar, saveHeader, savingHeader,
      saving, savingTemplate, imageInput, templateImageInput, messageTextarea, templateTextarea,
      messages, templates, historyLogs, customers,
      selectedCustomers, pickerSearch, pickerGroupFilter,
      pickerFilteredCustomers, isAllPickerSelected,
      toggleCustomerSelect, toggleSelectAll,
      copyMessageToMine, copyPackToMine,
      userMsgSettings, viewerUserSetting, getUserMsgSetting, saveUserMsgSetting, editOriginalMessage,
      userPackSettings, showPackViewer, viewingPack, viewingPackItems, viewerPackSetting,
      getUserPackSetting, openPackViewer, saveUserPackSetting, editOriginalPack,
      isPackItemSelected, togglePackItem, selectedPackItemCount,
      editingMessage, editingTemplate, availableVariables, viewingTemplate,
      menuState, menuStyle, isAdmin,
      getSearchPlaceholder, commonMessages, myMessages, filteredTemplates, 
      filteredHistory, targetCustomerCount,
      contentLength, totalContentLength, messageType, messageTypeLabel,
      formatKRPhone, truncate, formatDate, getScheduleText,
      getTriggerLabel, getTriggerIcon, getCategoryLabel, getCategoryIcon,
      getSendTypeLabel, getStatusLabel, getMessageTypeFromMsg, getMessageTypeLabel, getFullMessageLength,
      canEditMessage, editFromViewer, closeMessageEditor, closeTemplateEditor,
      openMenu, closeMenu, handleMenuAction,
      openMessageViewer, openTemplateViewer, openTemplateEditor, useTemplate, saveTemplate,
      triggerTemplateImageUpload, handleTemplateImageUpload, insertTemplateVariable,
      openMessageEditor, triggerImageUpload, handleImageUpload,
      insertVariable, saveMessage,
      getMbid
    };
  }
}
</script>


<style scoped>
/* ===== FULLSCREEN LAYOUT ===== */
.crm-fullscreen {
  position: fixed;
  inset: 0;
  background: var(--color-background);
  display: flex;
  flex-direction: column;
  z-index: var(--z-index-middle);
}

/* ===== APP BAR ===== */
.app-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.app-bar-back {
  width: 40px; height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 50%;
  color: var(--color-text-primary);
  font-size: 20px;
  cursor: pointer;
  transition: background 0.15s;
}

.app-bar-back:hover {
  background: var(--color-background-light);
}

.app-bar-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: -0.3px;
}

/* ===== AUTO SEND TOGGLE ===== */
.auto-send-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 20px;
  background: var(--color-background-light);
  transition: all 0.2s;
  user-select: none;
}

.auto-send-toggle.on {
  background: rgba(52, 199, 89, 0.12);
}

.auto-send-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  white-space: nowrap;
}

.auto-send-toggle.on .auto-send-label {
  color: #34c759;
}

.auto-send-switch {
  position: relative;
  width: 36px;
  height: 20px;
  background: var(--color-text-tertiary);
  border-radius: 10px;
  transition: background 0.2s;
}

.auto-send-toggle.on .auto-send-switch {
  background: #34c759;
}

.auto-send-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.auto-send-toggle.on .auto-send-knob {
  transform: translateX(16px);
}

.app-bar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--color-background-light);
  border-radius: 20px;
  transition: all 0.2s;
  width: 160px;
}

.search-box.expanded {
  width: 220px;
  box-shadow: 0 0 0 2px var(--color-brand);
}

.search-box i {
  color: var(--color-text-secondary);
  font-size: 16px;
  flex-shrink: 0;
}

.search-box input {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  font-size: 13px;
  outline: none;
  min-width: 0;
}

.search-box input::placeholder {
  color: var(--color-text-secondary);
}

.sort-chip {
  padding: 8px 12px;
  border: none;
  background: var(--color-background-light);
  border-radius: 20px;
  color: var(--color-text-primary);
  font-size: 13px;
  cursor: pointer;
  outline: none;
}

/* ===== TAB NAVIGATION ===== */
.tab-nav {
  display: flex;
  gap: 6px;
  padding: 10px 16px;
  background: var(--color-background);
  flex-shrink: 0;
  overflow-x: auto;
}

.tab-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--color-background-light);
  border: none;
  border-radius: 20px;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.tab-chip i {
  font-size: 16px;
}

.tab-chip:hover {
  background: var(--color-surface-reverse);
}

.tab-chip.active {
  background: var(--color-brand);
  color: white;
}

.tab-count {
  padding: 1px 7px;
  background: rgba(255,255,255,0.3);
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.tab-chip:not(.active) .tab-count {
  background: var(--color-surface-reverse);
}

/* ===== CONTENT AREA ===== */
.content-area {
  flex: 1;
  overflow: hidden;
}

.tab-panel {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 100px;
}

/* ===== FLOATING ACTION BUTTON ===== */
.fab-container {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.fab-scrim {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  z-index: -1;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fab-main {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: #f5a623;
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(245,166,35,0.4);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 2;
}

.fab-main:hover {
  transform: scale(1.06);
  box-shadow: 0 6px 20px rgba(245,166,35,0.5);
}

.fab-main.open {
  transform: rotate(45deg);
  background: #e09516;
}

.fab-menu {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 14px;
  position: relative;
  z-index: 2;
}

.fab-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.fab-menu-label {
  padding: 8px 14px;
  background: var(--color-background);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  box-shadow: 0 2px 10px rgba(0,0,0,0.12);
  white-space: nowrap;
}

.fab-menu-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
  box-shadow: 0 3px 10px rgba(0,0,0,0.15);
}

.fab-menu-icon.auto {
  background: var(--color-brand);
}

.fab-menu-icon.instant {
  background: #10b981;
}

.fab-menu-icon.group {
  background: #8b5cf6;
}

.fab-menu-item:hover .fab-menu-label {
  color: var(--color-brand);
}

.fab-menu-item:hover .fab-menu-icon {
  transform: scale(1.08);
}

/* FAB menu transition */
.fab-menu-enter-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.fab-menu-leave-active {
  transition: all 0.15s ease-in;
}

.fab-menu-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.8);
}

.fab-menu-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.9);
}

/* ===== GROUP TAG SELECTION ===== */
.group-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.group-tag-list.compact {
  gap: 6px;
}

.group-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border: 1.5px solid var(--color-background-light);
  border-radius: 20px;
  background: var(--color-background);
  color: var(--color-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.group-tag-chip i {
  font-size: 15px;
}

.group-tag-chip:hover {
  border-color: #8b5cf6;
  color: #8b5cf6;
}

.group-tag-chip.active {
  background: #f3f0ff;
  border-color: #8b5cf6;
  color: #8b5cf6;
  font-weight: 600;
}

.group-tag-chip.small {
  padding: 5px 10px;
  font-size: 12px;
}

.group-tag-chip.small i {
  font-size: 13px;
}

.group-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 10px 14px;
  background: #f3f0ff;
  border-radius: 10px;
  font-size: 13px;
  color: #6d28d9;
}

.group-summary i {
  font-size: 16px;
}

.group-summary strong {
  font-weight: 700;
}

.group-target-area {
  margin-bottom: 12px;
  padding: 12px;
  background: var(--color-background-light);
  border-radius: 12px;
}

.target-tag-info {
  color: #8b5cf6;
  font-weight: 600;
  font-size: 12px;
}

.r-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.r-tag-chip {
  padding: 1px 6px;
  background: #f3f0ff;
  color: #7c3aed;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

/* ===== MESSAGE SECTIONS ===== */
.msg-section {
  margin-bottom: 24px;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 4px;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.section-label i {
  font-size: 18px;
  color: var(--color-brand);
}

.badge-count {
  padding: 2px 10px;
  background: var(--color-background-light);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
}

/* ===== MESSAGE CARDS ===== */
.card-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.msg-card {
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 14px;
  background: var(--color-background-light);
  border-radius: 14px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
}

.msg-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}

.msg-card.paused {
  opacity: 0.5;
}

.card-indicator {
  width: 4px;
  border-radius: 4px;
  flex-shrink: 0;
  align-self: stretch;
}

.card-indicator.common {
  background: var(--color-brand);
}

.card-indicator.mine {
  background: #60a5fa;
}

.card-thumb {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
}

.card-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  border-radius: 12px;
}

.thumb-icon.birthday { background: linear-gradient(135deg, #ec4899, #f472b6); }
.thumb-icon.contract_anniversary { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.thumb-icon.payment_due { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.thumb-icon.holiday { background: linear-gradient(135deg, #10b981, #34d399); }
.thumb-icon.custom { background: linear-gradient(135deg, #6366f1, #818cf8); }
.thumb-icon.instant { background: linear-gradient(135deg, #10b981, #34d399); }

.card-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-body h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 30px;
}

.card-top-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-top-row h4 {
  flex: 1;
}

.pill {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.common-pill {
  background: var(--color-brand);
  color: white;
}

.card-desc {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  background: var(--color-background);
  border-radius: 6px;
  font-size: 11px;
  color: var(--color-text-secondary);
}

.chip i {
  font-size: 12px;
}

.chip.warn {
  background: #fef3c7;
  color: #b45309;
}

.card-menu-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px; height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--color-text-secondary);
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
}

.msg-card:hover .card-menu-btn {
  opacity: 1;
}

.card-menu-btn:hover {
  background: var(--color-background);
  color: var(--color-text-primary);
}

/* ===== EMPTY STATE ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-state.grid-empty {
  grid-column: span 2;
}

.empty-icon {
  width: 72px; height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background-light);
  border-radius: 50%;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 32px;
  color: var(--color-text-secondary);
  opacity: 0.5;
}

.empty-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.empty-desc {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

/* ===== TEMPLATE GRID ===== */
.add-tpl-btn {
  width: 100%;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--color-background-light);
  border: 2px dashed var(--color-text-secondary);
  border-radius: 14px;
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 16px;
  transition: all 0.2s;
}

.add-tpl-btn i {
  font-size: 20px;
}

.add-tpl-btn:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.tpl-card {
  background: var(--color-background-light);
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  transition: all 0.2s;
}

.tpl-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.08);
}

.tpl-visual {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  cursor: pointer;
}

.tpl-visual img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tpl-icon-bg {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 44px;
  color: white;
}

.tpl-icon-bg.birthday { background: linear-gradient(135deg, #ec4899, #f472b6); }
.tpl-icon-bg.anniversary { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.tpl-icon-bg.payment { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.tpl-icon-bg.greeting { background: linear-gradient(135deg, #10b981, #34d399); }
.tpl-icon-bg.promotion { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.tpl-icon-bg.general { background: linear-gradient(135deg, #6b7280, #9ca3af); }

.tpl-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 8px;
  background: linear-gradient(to top, rgba(0,0,0,0.4), transparent 60%);
}

.tpl-cat-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  align-self: flex-start;
}

.tpl-cat-badge.birthday { background: rgba(236,72,153,0.9); }
.tpl-cat-badge.anniversary { background: rgba(139,92,246,0.9); }
.tpl-cat-badge.payment { background: rgba(245,158,11,0.9); }
.tpl-cat-badge.greeting { background: rgba(16,185,129,0.9); }
.tpl-cat-badge.promotion { background: rgba(59,130,246,0.9); }
.tpl-cat-badge.general { background: rgba(107,114,128,0.9); }

.tpl-mine-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  background: var(--color-brand);
  color: white;
}

.tpl-info {
  padding: 12px;
}

.tpl-info h4 {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tpl-info p {
  margin: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tpl-more-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px; height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.4);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.tpl-card:hover .tpl-more-btn {
  opacity: 1;
}

/* ===== HISTORY LIST ===== */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: var(--color-background-light);
  border-radius: 12px;
  transition: all 0.15s;
}

.history-row.failed {
  opacity: 0.7;
}

.history-status-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
}

.history-status-dot.pending { background: #f59e0b; }
.history-status-dot.success { background: #10b981; }
.history-status-dot.failed { background: #ef4444; }

.history-body {
  flex: 1;
  min-width: 0;
}

.history-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.history-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.history-date {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.history-content {
  margin: 0 0 8px;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.4;
}

.history-tags {
  display: flex;
  gap: 6px;
}

.htag {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
}

.htag.auto { background: #dbeafe; color: #2563eb; }
.htag.scheduled { background: #fef3c7; color: #b45309; }
.htag.instant { background: #ecfdf5; color: #059669; }
.htag.pending { background: #fef3c7; color: #b45309; }
.htag.success { background: #dcfce7; color: #16a34a; }
.htag.failed { background: #fee2e2; color: #dc2626; }

/* ===== DROPDOWN MENU ===== */
.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
}

.dropdown-sheet {
  min-width: 150px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  overflow: hidden;
  padding: 4px;
}

.dropdown-sheet button {
  width: 100%;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  border-radius: 8px;
  color: var(--color-text-primary);
  font-size: 13px;
  cursor: pointer;
  text-align: left;
}

.dropdown-sheet button:hover {
  background: var(--color-background-light);
}

.dropdown-sheet button.danger {
  color: #ef4444;
}

/* ===== BOTTOM SHEET (뷰어/제외/헤더) ===== */
.sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: var(--z-index-top);
}

.bottom-sheet {
  width: 100%;
  max-width: 500px;
  max-height: 85vh;
  background: var(--color-background);
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bottom-sheet.tall {
  max-height: 75vh;
}

.sheet-handle {
  width: 36px;
  height: 4px;
  background: var(--color-background-light);
  border-radius: 4px;
  margin: 8px auto 4px;
}

.sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
}

.sheet-header h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.sheet-close {
  width: 32px; height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background-light);
  border: none;
  border-radius: 50%;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 16px;
}

.sheet-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px;
}

.sheet-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid var(--color-background-light);
}

/* ===== SMS PREVIEW ===== */
.sms-preview-area {
  padding: 20px;
  background: var(--color-background-light);
  border-radius: 14px;
  margin-bottom: 16px;
}

.sms-bubble {
  max-width: 260px;
  background: white;
  border-radius: 18px 18px 18px 4px;
  padding: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.sms-image {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 8px;
}

.sms-header-text {
  margin: 0 0 8px;
  padding-bottom: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #888;
  white-space: pre-wrap;
  word-break: break-word;
  border-bottom: 1px solid #f0f0f0;
}

.sms-body-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #1a1a1a;
  white-space: pre-wrap;
  word-break: break-word;
}

.sms-time {
  display: block;
  margin-top: 6px;
  font-size: 11px;
  color: #bbb;
  text-align: right;
}

/* ===== INFO GRID ===== */
.info-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-cell {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: var(--color-background-light);
  border-radius: 10px;
}

.info-key {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.info-val {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.trigger-pill, .type-pill {
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.trigger-pill.birthday { background: #fce7f3; color: #be185d; }
.trigger-pill.contract_anniversary { background: #ede9fe; color: #6d28d9; }
.trigger-pill.payment_due { background: #fef3c7; color: #b45309; }
.trigger-pill.holiday { background: #d1fae5; color: #047857; }
.trigger-pill.custom { background: #e0e7ff; color: #4338ca; }
.trigger-pill.instant { background: #ecfdf5; color: #059669; }

.type-pill.sms { background: #f3e8ff; color: #7c3aed; }
.type-pill.lms { background: #dbeafe; color: #2563eb; }
.type-pill.mms { background: #dcfce7; color: #16a34a; }

.sub-text {
  font-size: 11px;
  color: var(--color-text-secondary);
  font-weight: 400;
}

/* ===== TEMPLATE VIEWER ===== */
.tpl-viewer-img {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
}

.tpl-viewer-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tpl-viewer-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 56px;
  color: white;
}

.tpl-viewer-placeholder.birthday { background: linear-gradient(135deg, #ec4899, #f472b6); }
.tpl-viewer-placeholder.anniversary { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.tpl-viewer-placeholder.payment { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.tpl-viewer-placeholder.greeting { background: linear-gradient(135deg, #10b981, #34d399); }
.tpl-viewer-placeholder.promotion { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.tpl-viewer-placeholder.general { background: linear-gradient(135deg, #6b7280, #9ca3af); }

.tpl-viewer-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.use-count-text {
  font-size: 12px;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.tpl-viewer-text label {
  display: block;
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
}

.text-block {
  padding: 14px;
  background: var(--color-background-light);
  border-radius: 12px;
  font-size: 13px;
  color: var(--color-text-primary);
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 150px;
  overflow-y: auto;
}

/* ===== FULLSCREEN MODAL (편집 모달) ===== */
.fullscreen-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--color-background);
  z-index: var(--z-index-top);
}

.fullscreen-modal {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  max-width: 600px;
  margin: 0 auto;
}

.fm-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-background-light);
  flex-shrink: 0;
}

.fm-back {
  width: 36px; height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 50%;
  color: var(--color-text-primary);
  font-size: 20px;
  cursor: pointer;
}

.fm-back:hover {
  background: var(--color-background-light);
}

.fm-header h3 {
  margin: 0;
  flex: 1;
  font-size: 17px;
  font-weight: 700;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.fm-header-right {
  display: flex;
  gap: 8px;
}

.fm-draft {
  padding: 8px 14px;
  background: var(--color-background-light);
  border: none;
  border-radius: 8px;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.fm-save {
  padding: 8px 16px;
  background: var(--color-brand);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.fm-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fm-save.send-btn {
  background: #10b981;
  display: flex;
  align-items: center;
  gap: 6px;
}

.fm-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ===== FORM ELEMENTS ===== */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group > label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group input[type="date"],
.form-group input[type="time"],
.form-group input[type="datetime-local"],
.form-group select,
.form-group textarea {
  padding: 12px 14px;
  border: 1px solid var(--color-background-light);
  border-radius: 12px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: var(--color-brand);
}

.form-group textarea {
  resize: none;
  line-height: 1.6;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row .form-group {
  flex: 1;
}

/* Image Upload */
.img-upload-area {
  width: 100%;
  aspect-ratio: 16/9;
  border: 2px dashed var(--color-background-light);
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.img-upload-area:hover {
  border-color: var(--color-brand);
}

.img-upload-area img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-empty {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--color-text-secondary);
}

.upload-empty i { font-size: 28px; }
.upload-empty span { font-size: 13px; }
.upload-empty small { font-size: 11px; opacity: 0.7; }

.img-remove {
  position: absolute;
  top: 8px; right: 8px;
  width: 28px; height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.6);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
}

.img-remove:hover { background: #ef4444; }

/* Toggle Row */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--color-text-primary);
}

.toggle-row input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--color-brand);
}

.toggle-text {
  font-weight: 500;
}

.highlight-toggle {
  padding: 14px;
  background: var(--color-background-light);
  border-radius: 12px;
  border: 1px solid transparent;
  transition: all 0.2s;
}

.highlight-toggle:has(input:checked) {
  background: rgba(var(--color-brand-rgb), 0.1);
  border-color: var(--color-brand);
}

.highlight-toggle i {
  color: var(--color-brand);
}

.hint-text {
  font-size: 12px;
  color: var(--color-text-secondary);
}

/* Trigger Days */
.trigger-days-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trigger-days-input input { width: 80px; }

.trigger-days-input span {
  font-size: 13px;
  color: var(--color-text-secondary);
}

/* Content Meta */
.content-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.char-counter {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.char-counter.over {
  color: #ef4444;
  font-weight: 600;
}

/* Variable Chips */
.var-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 6px;
}

.var-label {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.var-chips code {
  padding: 4px 10px;
  background: var(--color-background-light);
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  color: var(--color-text-primary);
}

.var-chips code:hover {
  background: var(--color-brand);
  color: white;
}

/* Sender Block */
.sender-block {
  background: var(--color-background-light);
  border-radius: 14px;
  padding: 14px;
}

.sender-preview {
  background: var(--color-background);
  border-radius: 10px;
  padding: 12px;
  position: relative;
  margin-top: 8px;
}

.sender-preview pre {
  margin: 0;
  font-family: inherit;
  font-size: 12px;
  color: var(--color-text-secondary);
  white-space: pre-wrap;
  line-height: 1.5;
}

.edit-sender-btn {
  position: absolute;
  top: 8px; right: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--color-background-light);
  border: none;
  border-radius: 6px;
  color: var(--color-text-secondary);
  font-size: 11px;
  cursor: pointer;
}

.edit-sender-btn:hover {
  background: var(--color-brand);
  color: white;
}

/* Target Bar */
.target-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  background: var(--color-background-light);
  border-radius: 12px;
}

.target-count {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.exclude-num {
  color: #ef4444;
  font-size: 12px;
}

.btn-outline-sm {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: var(--color-background);
  border: 1px solid var(--color-background-light);
  border-radius: 8px;
  color: var(--color-text-secondary);
  font-size: 12px;
  cursor: pointer;
}

.btn-outline-sm:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

/* Recipient Search/List */
.recipient-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--color-background-light);
  border-radius: 12px;
  margin-bottom: 10px;
}

.recipient-search-bar i {
  color: var(--color-text-secondary);
  font-size: 16px;
}

.recipient-search-bar input {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  font-size: 13px;
  outline: none;
}

.recipient-select-all {
  padding: 8px 0;
  border-bottom: 1px solid var(--color-background-light);
  margin-bottom: 8px;
}

.recipient-scroll {
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.recipient-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--color-background-light);
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
}

.recipient-row:hover {
  background: var(--color-surface-reverse);
}

.recipient-row.checked {
  background: rgba(16,185,129,0.08);
  border-color: rgba(16,185,129,0.25);
}

.recipient-row.exclude-mode.checked {
  background: rgba(239,68,68,0.08);
  border-color: rgba(239,68,68,0.25);
}

.recipient-row input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #10b981;
}

.recipient-row.exclude-mode input[type="checkbox"] {
  accent-color: #ef4444;
}

.r-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.r-phone {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.picker-group-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.picker-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.select-all-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--color-text-secondary);
  cursor: pointer;
  white-space: nowrap;
}

.select-all-toggle input[type="checkbox"] {
  accent-color: #10b981;
}

.selected-cnt {
  color: #10b981;
  font-weight: 500;
  margin-left: 4px;
}

.empty-mini {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  color: var(--color-text-secondary);
}

.empty-mini i { font-size: 28px; margin-bottom: 8px; opacity: 0.5; }
.empty-mini p { margin: 0; font-size: 13px; }

.exclude-count-label {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-right: auto;
}

/* ===== HEADER EDITOR ===== */
.header-var-area {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding: 12px;
  background: var(--color-background-light);
  border-radius: 12px;
}

.header-var-area code {
  padding: 5px 12px;
  background: var(--color-background);
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}

.header-var-area code:hover {
  background: var(--color-brand);
  color: white;
}

.reset-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px;
  background: none;
  border: 1px dashed var(--color-text-secondary);
  border-radius: 12px;
  color: var(--color-text-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.reset-btn:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

/* ===== BUTTONS ===== */
.btn-ghost {
  padding: 10px 20px;
  background: var(--color-background-light);
  border: none;
  border-radius: 10px;
  color: var(--color-text-primary);
  font-size: 14px;
  cursor: pointer;
}

.btn-ghost:hover {
  background: var(--color-surface-reverse);
}

.btn-filled {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: var(--color-brand);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-filled:hover {
  opacity: 0.9;
}

.btn-filled:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== TRANSITION ===== */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* ===== RESPONSIVE ===== */

/* ===== 🎁 GIFT ATTACH ===== */
.gift-section {
  border: 1px solid var(--color-background-light);
  border-radius: 12px;
  padding: 12px;
  transition: all 0.2s;
}

.gift-section:has(input:checked) {
  border-color: #f59e0b;
  background: rgba(245, 158, 11, 0.04);
}

.gift-toggle i {
  color: #f59e0b;
  font-size: 18px;
}

.gift-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--color-background-light);
}

.gift-stock-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  background: var(--color-background-light);
  border-radius: 10px;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.gift-stock-info strong {
  color: #f59e0b;
  font-weight: 600;
}

.gift-refresh-btn {
  margin-left: auto;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  border: none;
  border-radius: 8px;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.gift-refresh-btn:hover {
  color: #f59e0b;
  background: var(--color-surface-reverse);
}

.gift-product-select {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.gift-product-select label {
  font-size: 12px;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.gift-product-select select {
  padding: 8px 12px;
  border: 1px solid var(--color-background-light);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: 13px;
}

.gift-max-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.gift-max-row > label {
  font-size: 12px;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.gift-max-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gift-max-control input {
  width: 70px;
  padding: 8px 10px;
  border: 1px solid var(--color-background-light);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: 13px;
  text-align: center;
}

.gift-max-hint {
  font-size: 12px;
  color: var(--color-text-tertiary);
}

.gift-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  background: rgba(245, 158, 11, 0.06);
  border-radius: 8px;
  font-size: 11px;
  color: var(--color-text-tertiary);
  line-height: 1.4;
}

.gift-note i {
  color: #f59e0b;
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* slide-down transition */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 400px;
}

/* ===== END GIFT ===== */

.chip.gift {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.chip.gift i {
  color: #f59e0b;
}

.chip.pack {
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
}

.fab-menu-icon.pack {
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
}

.msg-card.disabled {
  opacity: 0.45;
}

/* ===== 원본 배지 ===== */
.pill.origin-pill {
  background: rgba(234, 88, 12, 0.12);
  color: #ea580c;
  font-weight: 700;
  font-size: 11px;
}

.chip.origin-chip {
  color: #ea580c;
  background: rgba(234, 88, 12, 0.1);
  font-weight: 600;
}

/* ===== 원본 수정 경고 배너 ===== */
.origin-warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: rgba(234, 88, 12, 0.08);
  border: 1px solid rgba(234, 88, 12, 0.25);
  border-radius: 12px;
  margin-bottom: 16px;
}

.origin-warning-banner > i {
  font-size: 22px;
  color: #ea580c;
  flex-shrink: 0;
  margin-top: 1px;
}

.origin-warning-banner strong {
  display: block;
  font-size: 13px;
  color: #ea580c;
  margin-bottom: 2px;
}

.origin-warning-banner p {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.4;
}

/* ===== 원본 수정 버튼 ===== */
.btn-origin-edit {
  background: #ea580c !important;
}

/* ===== 팩 뷰어 ===== */
.pack-viewer-sheet .sheet-body {
  max-height: 65vh;
  overflow-y: auto;
}

.pack-items-preview {
  margin: 16px 0;
}

.pv-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-secondary);
  margin-bottom: 10px;
}

.pv-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: var(--color-background-light);
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.pv-item.pv-inactive {
  opacity: 0.35;
}

.pv-check {
  cursor: pointer;
  flex-shrink: 0;
  padding-top: 2px;
}

.pv-check input { display: none; }

.pv-check-box {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background: transparent;
}

.pv-check-box.checked {
  background: var(--color-brand);
  border-color: var(--color-brand);
}

.pv-check-box i {
  font-size: 12px;
  color: #fff;
}

.pv-selected-count {
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-brand);
}

.pv-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color-brand);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pv-content {
  flex: 1;
  min-width: 0;
}

.pv-content strong {
  font-size: 13px;
  display: block;
  margin-bottom: 2px;
}

.pv-content p {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.4;
}

.pv-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

/* ===== 내 발송 설정 섹션 (뷰어) ===== */
.user-settings-section {
  background: var(--color-background-light);
  border-radius: 14px;
  padding: 16px;
  margin-top: 16px;
  border: 1px solid var(--color-border);
}

.us-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--color-brand);
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-border);
}

.us-header i {
  font-size: 18px;
}

.us-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0,0,0,0.04);
}

.us-row:last-of-type {
  border-bottom: none;
}

.us-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.us-action {
  display: flex;
  align-items: center;
  gap: 8px;
}

.us-action strong {
  font-size: 13px;
  color: var(--color-brand);
}

/* 미니 토글 */
.mini-toggle {
  cursor: pointer;
  display: inline-flex;
}

.mini-toggle input { display: none; }

.mini-toggle-track {
  width: 42px;
  height: 24px;
  border-radius: 12px;
  background: #ccc;
  position: relative;
  transition: background 0.2s;
}

.mini-toggle.on .mini-toggle-track {
  background: var(--color-brand);
}

.mini-toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

.mini-toggle.on .mini-toggle-knob {
  transform: translateX(18px);
}

/* 선물 상세 */
.us-gift-detail {
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.us-select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 13px;
  background: var(--color-background);
  color: var(--color-text-primary);
}

.us-gift-max {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.us-gift-max input {
  width: 60px;
  padding: 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  text-align: center;
  font-size: 13px;
}

/* 설정 저장 버튼 */
.us-save-btn {
  width: 100%;
  margin-top: 14px;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: var(--color-brand);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: opacity 0.15s;
}

.us-save-btn:hover { opacity: 0.9; }
.us-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.pack-summary-section {
  background: var(--color-background-light);
  border-radius: 12px;
  padding: 16px;
}

.pack-summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.pack-summary-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.pack-summary-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  background: var(--color-background);
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.settings-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 14px;
  color: var(--color-brand);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--color-brand);
}

.settings-section-label i {
  font-size: 18px;
}

.msg-settings-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--color-text-primary);
  padding: 10px 12px;
  background: var(--color-background-light);
  border-radius: 8px;
  margin-bottom: 12px;
}

.msg-settings-title i {
  color: var(--color-brand);
}

.card-indicator.pack {
  background: #8b5cf6;
}

.msg-card.completed {
  opacity: 0.55;
}

.chip.completed {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.chip.completed i {
  color: #10b981;
}

.pack-thumb {
  background: rgba(139, 92, 246, 0.15) !important;
  color: #8b5cf6 !important;
}

.pill.pack-pill {
  background: rgba(139, 92, 246, 0.15);
  color: #8b5cf6;
}

/* ===== PACK EDITOR ===== */
.pack-items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.pack-items-header label {
  font-weight: 700;
  font-size: 14px;
  color: var(--color-text-primary);
}

.pack-item {
  background: var(--color-background-light);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 10px;
  border: 1px solid var(--color-border);
  transition: border-color 0.2s;
}

.pack-item:focus-within {
  border-color: var(--color-brand);
}

.pack-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.pack-item-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #8b5cf6;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pack-item-title {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  padding: 4px 0;
}

.pack-item-title::placeholder {
  color: var(--color-text-tertiary);
}

.pack-item-delete {
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.15s;
}

.pack-item-delete:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.pack-item textarea {
  width: 100%;
  border: none;
  background: transparent;
  font-size: 13px;
  color: var(--color-text-primary);
  resize: none;
  line-height: 1.6;
  font-family: inherit;
}

.pack-item textarea::placeholder {
  color: var(--color-text-tertiary);
}

.pack-item-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.pack-item-thumb {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  object-fit: cover;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.15s;
}

.pack-item-thumb:hover {
  opacity: 0.5;
}

.btn-text {
  background: none;
  border: none;
  color: var(--color-brand);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.15s;
}

.btn-text:hover {
  background: rgba(var(--color-brand-rgb, 0,0,0), 0.08);
}

.label-hint {
  font-weight: 400;
  font-size: 11px;
  color: var(--color-text-tertiary);
}

.pack-name-input {
  margin-top: 6px;
}
@media (max-width: 480px) {
  .tab-chip {
    padding: 8px 12px;
    font-size: 12px;
  }

  .search-box {
    width: 120px;
  }

  .search-box.expanded {
    width: 160px;
  }

  .form-row {
    flex-direction: column;
  }

  .template-grid {
    gap: 8px;
  }

  .bottom-sheet {
    max-height: 90vh;
  }
}

/* ===== 선물 배정 모드 탭 ===== */
.gift-assign-mode {
  margin-bottom: 12px;
}

.gift-assign-mode label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
  display: block;
}

.gift-mode-tabs {
  display: flex;
  gap: 0;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  overflow: hidden;
}

.gift-mode-tab {
  flex: 1;
  padding: 10px 12px;
  border: none;
  background: var(--color-background);
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}

.gift-mode-tab + .gift-mode-tab {
  border-left: 1px solid var(--color-border);
}

.gift-mode-tab.active {
  background: var(--color-brand);
  color: #fff;
}

.gift-mode-tab i {
  font-size: 16px;
}

/* ===== 개별 선물 지정 ===== */
.gift-individual-section {
  margin-top: 8px;
}

.gift-individual-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.gift-individual-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.gift-individual-label i {
  color: var(--color-brand);
}

.gift-bulk-apply-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: rgba(var(--color-brand-rgb, 0,0,0), 0.04);
  border-radius: 10px;
  margin-bottom: 10px;
}

.gift-bulk-apply-bar .us-select {
  flex: 1;
}

.gift-bulk-apply-bar .btn-outline-sm.cancel {
  color: var(--color-text-tertiary);
  border-color: var(--color-text-tertiary);
}

.gift-individual-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 280px;
  overflow-y: auto;
  padding: 2px 0;
}

.gift-assign-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: var(--color-background-light);
  border-radius: 10px;
  border: 1px solid var(--color-border);
  transition: border-color 0.15s;
}

.gift-assign-row:focus-within {
  border-color: var(--color-brand);
}

.gift-assign-person {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.gift-assign-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gift-assign-grade {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

.gift-assign-grade.mrank-badge {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.gift-assign-grade.vip-1 {
  background: rgba(251, 191, 36, 0.15);
  color: #b45309;
}

.gift-assign-grade.vip-2 {
  background: rgba(168, 85, 247, 0.15);
  color: #7c3aed;
}

.gift-assign-grade.vip-3 {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.gift-assign-select {
  width: 140px;
  flex-shrink: 0;
  padding: 6px 8px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 12px;
  background: var(--color-background);
  color: var(--color-text-primary);
}

.gift-individual-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 12px;
  background: rgba(16, 185, 129, 0.08);
  border-radius: 8px;
  font-size: 12px;
  color: #059669;
}

.gift-individual-summary i {
  font-size: 14px;
}

/* ===== 고객 등급 필터 ===== */
.grade-filter-section {
  margin-bottom: 12px;
}

.grade-filter-section:last-child {
  margin-bottom: 0;
}

.grade-filter-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
  padding-bottom: 4px;
}

.grade-filter-label i {
  font-size: 14px;
}

.grade-count {
  font-size: 10px;
  font-weight: 700;
  background: rgba(0,0,0,0.08);
  color: var(--color-text-tertiary);
  padding: 1px 5px;
  border-radius: 8px;
  margin-left: 2px;
}

.group-tag-chip.active .grade-count {
  background: rgba(255,255,255,0.25);
  color: #fff;
}

/* VIP 칩 색상 */
.vip-chip-1.active {
  background: #fbbf24 !important;
  border-color: #fbbf24 !important;
  color: #78350f !important;
}

.vip-chip-2.active {
  background: #a855f7 !important;
  border-color: #a855f7 !important;
  color: #fff !important;
}

.vip-chip-3.active {
  background: #ef4444 !important;
  border-color: #ef4444 !important;
  color: #fff !important;
}

/* 고객 리스트 등급 뱃지 */
.r-vip-badge {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.r-vip-badge.vip-1 {
  background: rgba(251, 191, 36, 0.2);
  color: #b45309;
}

.r-vip-badge.vip-2 {
  background: rgba(168, 85, 247, 0.2);
  color: #7c3aed;
}

.r-vip-badge.vip-3 {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.r-manage-badge {
  font-size: 9px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* 관리등급 색상 - 영업 단계별 그라데이션 */
.r-manage-badge.mrank-1 { background: rgba(156, 163, 175, 0.15); color: #6b7280; }  /* PB 홍보 */
.r-manage-badge.mrank-2 { background: rgba(59, 130, 246, 0.12); color: #2563eb; }   /* PS 가망 */
.r-manage-badge.mrank-3 { background: rgba(99, 102, 241, 0.12); color: #4f46e5; }   /* TA 연락 */
.r-manage-badge.mrank-4 { background: rgba(168, 85, 247, 0.12); color: #7c3aed; }   /* AP 접근 */
.r-manage-badge.mrank-5 { background: rgba(236, 72, 153, 0.12); color: #db2777; }   /* FF 설계 */
.r-manage-badge.mrank-6 { background: rgba(16, 185, 129, 0.12); color: #059669; }   /* PC 체결 */
.r-manage-badge.mrank-7 { background: rgba(245, 158, 11, 0.12); color: #d97706; }   /* CS 관리 */

/* 관계 유형 뱃지 */
.r-mtype-badge {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.r-mtype-badge.mtype-X { background: rgba(59, 130, 246, 0.15); color: #2563eb; }
.r-mtype-badge.mtype-Y { background: rgba(245, 158, 11, 0.15); color: #d97706; }
.r-mtype-badge.mtype-Z { background: rgba(16, 185, 129, 0.15); color: #059669; }

/* 관계 유형 칩 */
.mtype-chip-X.active { background: #3b82f6 !important; border-color: #3b82f6 !important; color: #fff !important; }
.mtype-chip-Y.active { background: #f59e0b !important; border-color: #f59e0b !important; color: #fff !important; }
.mtype-chip-Z.active { background: #10b981 !important; border-color: #10b981 !important; color: #fff !important; }

/* 관리등급 칩 */
.manage-rank-list {
  flex-wrap: wrap;
}

.manage-rank-chip {
  font-size: 11px !important;
}

.mrank-1.active { background: #9ca3af !important; border-color: #9ca3af !important; color: #fff !important; }
.mrank-2.active { background: #3b82f6 !important; border-color: #3b82f6 !important; color: #fff !important; }
.mrank-3.active { background: #6366f1 !important; border-color: #6366f1 !important; color: #fff !important; }
.mrank-4.active { background: #a855f7 !important; border-color: #a855f7 !important; color: #fff !important; }
.mrank-5.active { background: #ec4899 !important; border-color: #ec4899 !important; color: #fff !important; }
.mrank-6.active { background: #10b981 !important; border-color: #10b981 !important; color: #fff !important; }
.mrank-7.active { background: #f59e0b !important; border-color: #f59e0b !important; color: #fff !important; }
</style>